/**
 * Strategy Library — seeded from the Linked Accounting LLP
 * "Comprehensive Tax Planning" framework.
 *
 * Each strategy is classified along three axes:
 *   - categories: which client situation it fits (individual / real_estate / business)
 *   - goals: the firm's tax-planning waterfall (eliminate → reduce_rate → defer)
 *   - entityTypes: which tax forms / entity structures the strategy applies to
 *
 * calcType drives how savings are estimated:
 *   - "manual"              → planner enters the dollar reduction directly
 *   - "reduceTaxableIncome" → reduces taxable income by `inputs.amount`,
 *                              savings = amount × marginal rate
 *   - "taxCredit"           → direct dollar-for-dollar credit
 *   - "rateDelta"           → applies a different (usually capital-gains) rate
 *
 * For v1 most strategies are "manual" — the planner enters their own savings
 * figure based on the client's facts. Common formulaic strategies (Augusta rule,
 * hire kids, retirement contributions) have structured inputs.
 */

export const CATEGORIES = [
  { id: "individual", label: "Individual" },
  { id: "real_estate", label: "Real Estate" },
  { id: "business", label: "Business Owner / SE" },
];

export const GOALS = [
  {
    id: "eliminate",
    label: "Eliminate Tax",
    description: "Permanently remove the tax obligation",
    color: "emerald",
    rank: 1,
  },
  {
    id: "reduce_rate",
    label: "Reduce Tax Rate",
    description: "Convert ordinary income to preferential rates",
    color: "sky",
    rank: 2,
  },
  {
    id: "defer",
    label: "Defer Payment",
    description: "Push the tax obligation into a future period",
    color: "amber",
    rank: 3,
  },
];

export const ENTITY_TYPES = [
  { id: "1040", label: "1040 — Individual" },
  { id: "1120S", label: "1120-S — S Corp" },
  { id: "1065", label: "1065 — Partnership" },
  { id: "1120", label: "1120 — C Corp" },
];

// Helper to keep entries terse
const ALL_PASS = ["1040", "1120S", "1065"];
const ALL_ENTITY = ["1040", "1120S", "1065", "1120"];

// ─────────────────────────────────────────────────────────────────────
// Implementation timing / deadlines.
// Each strategy gets a `timing` block describing when work must be done.
//
// kind:
//   "year_end"         — must be completed in the calendar tax year (Dec 31)
//   "specific_date"    — fixed date in the tax year (e.g. 11/15 for plan setup)
//   "original_due"     — by original return due date (Apr 15 / Mar 15 entity)
//   "extended_due"     — by extended due date (Sep 15 / Oct 15)
//   "prior_to_filing"  — anytime before the return is filed
//   "ongoing"          — discipline / multi-year — no single deadline
//   "pre_transaction"  — must be in place before a triggering event (sale, hire, etc.)
//
// Some are state-specific (PTE elections vary). Where a date is given,
// it's the most restrictive common deadline; verify state-by-state.
// ─────────────────────────────────────────────────────────────────────
const TIMING = {
  // Individual / Eliminate
  muni_bonds: { kind: "ongoing", note: "Portfolio rebalance; coordinate with year-end review" },
  shift_to_idgt: { kind: "pre_transaction", note: "Before assets appreciate further; trust must exist before sale" },
  life_insurance_death_benefit: { kind: "ongoing", note: "Policy structure review; placement coordinated with estate plan" },
  foreign_earned_income_exclusion: { kind: "year_end", note: "Physical-presence / bona-fide residence test runs through 12/31" },
  max_non_retirement_employer_benefits: { kind: "specific_date", date: "11-15", note: "Open enrollment — typically Nov 1–15 each year" },
  donate_appreciated_capital_assets: { kind: "year_end", note: "Donation must be received & recorded by 12/31" },
  deduction_stacking_schedule_a: { kind: "year_end", note: "Bunching decision finalized by 12/31" },
  private_operating_foundation: { kind: "year_end", note: "Cash contributions in by 12/31 for current-year deduction; foundation formation can predate contributions" },
  crt_clt: { kind: "pre_transaction", note: "Trust funded before sale of contributed asset" },
  zero_tax_state_residency: { kind: "year_end", note: "Domicile change must be substantive before 12/31 of intended residency year" },
  ilit: { kind: "ongoing", note: "Crummey notices issued each premium contribution; setup well before any policy transfer" },
  tax_advantaged_accounts: { kind: "original_due", note: "HSA/IRA contribution by 4/15; 529 by 12/31 for current-year state deduction in many states" },
  educator_expenses: { kind: "year_end", note: "Eligible expenses incurred by 12/31" },
  primary_residence_exclusion: { kind: "pre_transaction", note: "Verify ownership/use tests met before closing" },
  tip_ot_classification: { kind: "year_end", note: "Coordinate with payroll provider before final W-2 cycle" },
  plan_agi_aca: { kind: "year_end", note: "AGI levers (Roth, retirement, HSA) all close 12/31; HSA/IRA extend to 4/15" },
  evaluate_tax_credits_individual: { kind: "year_end", note: "Most credits require year-end actions (energy installs, EV purchase)" },

  // Individual / Reduce + Defer
  structured_partnership: { kind: "pre_transaction", note: "Partnership structure documented before income event" },
  ltcg_holding_period: { kind: "year_end", note: "365-day holding period must be satisfied before sale" },
  profits_interest_for_hces: { kind: "pre_transaction", note: "Grant before equity appreciation event; safe-harbor profits interest in partnership" },
  flp_income_asset_shifting: { kind: "year_end", note: "Gift transfers and K-1 allocations finalized by 12/31" },
  tax_loss_harvesting: { kind: "year_end", note: "Sales must settle by 12/31; watch wash-sale 30-day window across year-end" },
  crypto_no_wash_sale: { kind: "year_end", note: "Realize losses by 12/31; immediate repurchase OK (no wash-sale rule yet)" },
  roth_conversion_timing: { kind: "year_end", note: "Conversion must be processed by 12/31 — recharacterization no longer available" },
  trust_k1_election: { kind: "original_due", note: "§645 election filed with first 1041; distribution decisions by 12/31 for K-1 deduction" },
  passive_nonpassive_classification: { kind: "extended_due", note: "Hour log / grouping election decided when return is prepared" },
  best_retirement_accounts: { kind: "specific_date", date: "12-31", note: "Plan must exist by 12/31 for elective deferrals; SECURE 2.0 allows new plan adoption to extended due date for employer contributions" },
  max_employer_retirement: { kind: "extended_due", note: "Solo 401(k) / SEP / DB contributions: by extended return due date" },
  installment_method_individual: { kind: "pre_transaction", note: "Installment sale agreement structured before closing" },
  deferred_sales_trust: { kind: "pre_transaction", note: "Trust + DST sale agreement in place before relinquished sale closes" },
  investment_portfolio_analysis: { kind: "ongoing", note: "Quarterly portfolio review; year-end rebalance" },
  tax_deferred_annuities: { kind: "year_end", note: "Annuity contract issued before year-end for current-year deferral" },

  // Real Estate
  stepped_up_basis: { kind: "ongoing", note: "Hold-to-death strategy; ensure title structure preserves step-up" },
  swap_til_you_drop: { kind: "pre_transaction", note: "Each §1031 leg: 45-day ID, 180-day close" },
  opportunity_zones: { kind: "specific_date", date: "180", note: "QOF investment within 180 days of original gain recognition" },
  conservation_easement: { kind: "year_end", note: "Easement deed recorded by 12/31; appraisal contemporaneous" },
  section_1250_allocation: { kind: "pre_transaction", note: "Negotiated in PSA before closing — Form 8594 must match" },
  niit_exclusion: { kind: "extended_due", note: "REPS hour log + grouping election filed with return" },
  "45L_credit": { kind: "extended_due", note: "DOE certification obtained before claiming on return" },
  augusta_rule: { kind: "year_end", note: "Days rented during the calendar year (≤14); document board minutes & FMV daily rate before 12/31" },
  avoid_dealer_classification: { kind: "ongoing", note: "Operating discipline — separate hold-vs-flip entities established before activity" },
  grouping_elections: { kind: "extended_due", note: "Election filed with first return after activities begin (binding once made)" },
  cost_segregation: { kind: "prior_to_filing", note: "Study can be done anytime before return is filed; §481(a) catch-up via Form 3115 for prior years" },
  residual_soil_fertility: { kind: "prior_to_filing", note: "Soil-fertility study ~55–60 days. Complete before filing; §180 requires active farming, §167/§168/§611 do not. Retroactive claims allowed up to 15 prior years via Form 3115." },
  short_term_rental: { kind: "year_end", note: "Material participation hours must be logged during the tax year" },
  section_179d: { kind: "extended_due", note: "Third-party certification obtained before claiming" },
  real_estate_pro: { kind: "year_end", note: "750+ hours and 50% test measured over the calendar year" },
  depreciation_timing_481a: { kind: "extended_due", note: "Method changes via Form 3115 with the return; bonus elections finalized at filing" },
  leveraged_equity_cashout: { kind: "ongoing", note: "Refinance timed to capital needs; not deadline-driven" },
  section_1031: { kind: "pre_transaction", note: "QI engaged BEFORE closing; 45-day ID, 180-day close from relinquishment" },

  // Business Owner
  reclass_costs_deductible: { kind: "extended_due", note: "Accountable plan adopted before reimbursements; bookkeeping reclass during return prep" },
  hire_kids: { kind: "year_end", note: "Wages paid during calendar year; document timesheet, W-2/1099 issued for kids" },
  college_kids_independent: { kind: "year_end", note: "Support & dependency tests measured over calendar year; AOTC requires student to be in qualified program" },
  qsbs_1202: { kind: "pre_transaction", note: "C-corp formed and stock issued BEFORE qualifying activity / liquidity event; 5-year hold" },
  nevada_trust_ccorp_sale: { kind: "pre_transaction", note: "Trust funded BEFORE binding LOI to avoid anticipatory assignment of income" },
  previously_disallowed_qbi: { kind: "extended_due", note: "Aggregation election filed with return; amend prior years if needed" },
  retain_divorced_spouse_owner: { kind: "pre_transaction", note: "Coordinated with divorce decree drafting" },
  avoid_max_sep: { kind: "specific_date", date: "12-31", note: "Solo 401(k) plan must exist by 12/31 for deferrals; SEP can be funded to extended due date" },
  entity_classification_se_tax: { kind: "specific_date", date: "03-15", note: "S-election (Form 2553): by 3/15 of intended year (or with first return); entity restructuring early in year" },
  reduce_office_comp: { kind: "year_end", note: "Reasonable comp study completed before final payroll run" },
  evaluate_tax_credits_business: { kind: "extended_due", note: "WOTC requires pre-screening BEFORE hire; R&D credit claimed on return" },
  state_nexus_analysis: { kind: "ongoing", note: "VDA timing — disclose before state contacts you; quarterly review" },
  cash_vs_accrual_timing: { kind: "extended_due", note: "Method change via Form 3115 with return; year-end timing of invoicing/expenses for cash method" },
  section_831b_captive: { kind: "year_end", note: "Captive must be formed and premiums paid in the tax year — typically Q4 setup" },
  sale_allocation: { kind: "pre_transaction", note: "Negotiated in asset purchase agreement; Form 8594 binding" },
  like_kind_business_assets: { kind: "pre_transaction", note: "§1031 timing rules apply; equipment trades not eligible post-TCJA" },
  delay_year_end_invoicing: { kind: "year_end", note: "Invoicing decisions finalized by 12/31; watch constructive receipt if cash was already earmarked" },
  prepay_year_end_bills: { kind: "year_end", note: "Payments must clear the bank by 12/31; §263 12-month rule for prepaid expenses" },
};

// PTE election deadlines vary by state — surface as a separate firmwide deadline
// rather than a strategy. Most states require election by the entity's
// original due date (3/15 for calendar-year pass-throughs).
export const PTE_ELECTION_DEADLINE_BY_STATE = {
  UT: { date: "Last day of 2nd month after year-end (≈Feb 28)", note: "Utah requires election with the timely-filed entity return" },
  CA: { date: "06-15", note: "CA: 1st payment due 6/15 of taxable year" },
  NY: { date: "03-15", note: "NY annual election by 3/15" },
  CO: { date: "Original return due date", note: "Election made on return" },
  ID: { date: "Original return due date", note: "Election made on return" },
  AZ: { date: "Original return due date", note: "Election made on return" },
  NM: { date: "Original return due date", note: "Election made on return" },
};

export const STRATEGIES = [
  // ───────────────────────────────────────────────
  // INDIVIDUAL — Eliminate
  // ───────────────────────────────────────────────
  {
    id: "muni_bonds",
    name: "Municipal Bonds in Portfolio Mix",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §103",
    shortDescription:
      "Hold tax-exempt municipal bonds to earn federally (and often state) tax-free interest.",
    longDescription:
      "Interest from state and local government bonds is generally exempt from federal tax under §103 and may be exempt from the issuing state's income tax. Best for high-bracket taxpayers when the after-tax muni yield exceeds the after-tax taxable yield.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "shift_to_idgt",
    name: "Shift Appreciating Assets to IDGT",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §671–679 (grantor trust rules)",
    shortDescription:
      "Sell appreciating assets to an Intentionally Defective Grantor Trust to freeze estate value while keeping income tax outside the trust.",
    longDescription:
      "An IDGT is treated as the grantor's for income tax (no gain on sale to trust, no recognition on swaps) but separate for estate tax. Future appreciation escapes both income and transfer tax to beneficiaries.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "life_insurance_death_benefit",
    name: "Life Insurance Death Benefits",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §101(a)",
    shortDescription:
      "Structure life insurance so death benefits pass income-tax-free to beneficiaries.",
    longDescription:
      "Amounts received by reason of death of the insured are excluded from gross income. Avoid the transfer-for-value rule and consider ILIT ownership to also remove from the estate.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "foreign_earned_income_exclusion",
    name: "Foreign Earned Income Exclusion",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §911",
    shortDescription:
      "Exclude up to the §911 limit of foreign earned income for U.S. citizens working abroad.",
    longDescription:
      "Requires bona fide residence or physical presence test. The exclusion is indexed annually; verify the current-year limit and the housing exclusion add-on.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "max_non_retirement_employer_benefits",
    name: "Max Use of Non-Retirement Employer Benefits",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §125, §129, §132",
    shortDescription:
      "Maximize cafeteria plan, dependent care FSA, transit/parking, and §132 fringe benefits.",
    longDescription:
      "These benefits are pre-tax and entirely escape income, FICA, and FUTA. Frequently underused by HCEs. Stack with HSA where eligible.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "donate_appreciated_capital_assets",
    name: "Donate Appreciated Capital Assets",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §170(e)",
    shortDescription:
      "Donate long-term appreciated stock or property in lieu of cash — deduct FMV and avoid the gain.",
    longDescription:
      "Taxpayer takes a charitable deduction at fair market value (subject to 30% AGI limit) AND avoids recognizing the built-in gain. Pair with a DAF for timing flexibility.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "deduction_stacking_schedule_a",
    name: "Deduction Stacking on Schedule A",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §63, §170",
    shortDescription:
      "Bunch deductible expenses (charity, property tax, medical) into alternating years to itemize above the standard deduction.",
    longDescription:
      "With the elevated standard deduction, alternating itemize/standard years using a DAF, prepaid property tax, or accelerated medical maximizes the lifetime deduction.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "private_operating_foundation",
    name: "Private Operating Foundation",
    categories: ["individual", "business"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §4942(j)(3)",
    shortDescription:
      "Family-controlled charitable foundation that operates its own programs — cash contributions deductible up to 60% of AGI, assets compound tax-free at 1.39% excise rate.",
    longDescription:
      "A Private Operating Foundation is a §4942(j)(3) entity that actively conducts its own charitable programs (operating a school, clinic, research institute, museum, etc.) rather than just making grants. Unlike a non-operating foundation, contributions qualify for the same higher AGI limits as public charity gifts (60% cash, 30% appreciated). Strong fit for taxpayers in the top bracket with $250K+ annual contribution capacity and a multi-decade philanthropic horizon.",
    calcType: "reduceTaxableIncome",
    inputs: [
      {
        key: "amount",
        label: "Annual contribution to POF",
        type: "money",
        default: 0,
      },
    ],
    defaultEstimate: 0,
    detailTemplate: {
      auditConfidence: {
        rating: "high",
        percent: ">90%",
        label: "Will be sustained by an IRS auditor",
      },
      idealClient:
        "Taxpayers capable of contributing >$250,000 during higher-income years who want to maximize deductions now — capturing the benefit at today's higher tax bracket — while directing charitable giving in later, lower-income years. Ideal for those with a lifetime philanthropic objective who want control over when and how their charitable dollars are deployed.",
      overview: [
        "A Private Operating Foundation (POF) is a special category of private foundation under IRC §4942(j)(3) that allows a high-income taxpayer to establish a permanent, family-controlled charitable entity, make fully tax-deductible contributions, and directly conduct meaningful philanthropic programs. Unlike a traditional private foundation — which primarily makes grants to other organizations — a POF is distinguished by its requirement to actively operate its own charitable programs, such as running an educational institute, funding direct medical research, operating an art museum, or administering community services.",
        "For a taxpayer in the 37% federal income tax bracket, a $250,000 annual cash contribution to a POF generates approximately $92,500 in federal income tax savings in the year of contribution. Investment assets held inside the foundation grow in a tax-advantaged environment — subject only to a modest 1.39% excise tax on net investment income — and can compound over decades into a substantial philanthropic endowment. This strategy is most powerful when used consistently over a multi-year horizon by taxpayers with both the income to sustain contributions and a genuine desire to build a lasting charitable legacy, potentially including the founder's children and grandchildren in the foundation's governance and programming.",
      ],
      flow: [
        {
          step: 1,
          title: "Taxpayer Earns Income",
          body: "High-income individual receives business income, investment gains, or compensation subject to federal & state income tax.",
        },
        {
          step: 2,
          title: "Cash Contribution to POF",
          body: "Taxpayer contributes cash (e.g., $250,000) to the Private Operating Foundation. Contribution is documented with a contemporaneous written acknowledgment.",
        },
        {
          step: 3,
          title: "Tax Deduction Claimed",
          body: "Taxpayer claims an itemized charitable deduction up to 60% of AGI for cash gifts. Excess carries forward up to 5 years. At a 37% bracket, a $250,000 gift yields ~$92,500 in annual tax savings.",
        },
        {
          step: 4,
          title: "POF Invests Assets",
          body: "The Foundation invests contributed funds in a diversified portfolio. All investment income, dividends, and capital gains inside the POF are exempt from income tax (only a 1.39% excise tax on net investment income applies).",
        },
        {
          step: 5,
          title: "Direct Charitable Program Activity",
          body: "The POF must spend at least 85% of its income (or the lesser of income or 3.33% of assets) directly on its own charitable programs and operations — NOT grants to other charities.",
        },
        {
          step: 6,
          title: "Remaining Assets Continue to Grow",
          body: "The ~15% of investment returns not spent on programs is retained and reinvested within the foundation, compounding tax-advantaged over time.",
        },
      ],
      forecast: {
        intro:
          "The table below models $250,000 in annual contributions, an 8% gross investment return, 85% of annual investment income deployed to active charitable programs, and 15% retained to compound inside the foundation. Tax savings are estimated at a 37% federal bracket. A 1.39% excise tax on net investment income is also reflected.",
        annualContribution: 250000,
        annualReturn: 0.08,
        programPct: 0.85,
        retainPct: 0.15,
        excisePct: 0.0139,
        marginalRate: 0.37,
        years: 10,
        milestones: {
          5: "Cumulative tax savings reach approximately $462,500. The foundation has deployed meaningful program dollars to charitable causes while its retained balance continues to grow — all from deductions taken in the taxpayer's highest-income years.",
          10: "Cumulative tax savings exceed $925,000 at the 37% bracket. The compounding retained balance inside the foundation becomes a self-sustaining philanthropic engine — growing and giving simultaneously, funded entirely by deductions captured in peak earning years.",
        },
        note: "Projections are illustrative only. Actual results depend on AGI, applicable tax rates, investment performance, and annual program spending. State income tax deductions (where available) may further enhance after-tax benefits.",
      },
      faqs: [
        {
          q: "Who is the ideal client for this strategy?",
          a: "High-net-worth individuals or business owners in the top federal tax bracket (37%) with consistent high income, a philanthropic mission, and the ability to contribute at least $250,000 annually. Ideal candidates have a multi-decade giving horizon and want control over how their charitable dollars are deployed.",
        },
        {
          q: "What is the minimum contribution needed to make this viable?",
          a: "We generally recommend a minimum annual contribution of $250,000 to justify the setup and ongoing compliance costs. Smaller amounts may not generate sufficient tax savings to offset the administrative burden. There is no IRS-imposed minimum, but economic viability typically requires this threshold.",
        },
        {
          q: "What are the one-time setup costs?",
          a: "Legal formation (articles, bylaws, conflict-of-interest policy): $2,500. IRS Form 1023 application for tax-exempt status: $4,500 in attorney/CPA fees plus a $600 user fee. State registration fees: $50–$500 depending on state. Total estimated setup: $7,050–$7,500.",
        },
        {
          q: "What are the annual compliance costs?",
          a: "Annual Form 990-PF preparation: $3,500. State charitable registration renewals: $100–$500/year. Investment management fees (if applicable): typically 0.5%–1.0% of assets. Board meeting documentation, grant record-keeping, and excise tax filing (Form 4720 if needed): $8,400. Total estimated annual cost: $12,400 + investment advisory fees.",
        },
        {
          q: "Is my contribution deductible immediately?",
          a: "Yes. Cash contributions to a qualified POF are deductible in the year made, up to 60% of your Adjusted Gross Income (AGI). Appreciated property gifts are generally limited to 30% of AGI. Any unused deduction carries forward for up to 5 tax years.",
        },
        {
          q: "Can I pay myself or family members a salary?",
          a: "Yes, within limits. Compensation to disqualified persons (the founder, family, and related entities) must be reasonable and commensurate with services actually rendered. Excessive compensation triggers excise taxes and jeopardizes tax-exempt status. All compensation must be documented and approved by independent board members.",
        },
        {
          q: "What are the annual distribution requirements?",
          a: "Unlike a Private Non-Operating Foundation (which must distribute 5% annually), a Private Operating Foundation must spend at least 85% of its adjusted net income (or 3.33% of assets, whichever is less) directly on its own active charitable programs. Distributions to other organizations generally do not count toward this requirement.",
        },
        {
          q: "What excise taxes apply?",
          a: "POFs are subject to a 1.39% excise tax on net investment income (dividends, interest, rents, capital gains). Self-dealing transactions can trigger excise taxes of 10%–200% of the transaction amount. These are significant penalties and must be avoided through careful compliance.",
        },
      ],
      cautions: [
        {
          title: "Self-Dealing Prohibition",
          body: "The IRS strictly prohibits financial transactions between the POF and 'disqualified persons' (founders, officers, directors, substantial contributors, and their family members). This includes loans, sales, leases, and services. Violations carry severe excise taxes — up to 200% of the transaction — and can result in loss of tax-exempt status.",
        },
        {
          title: "Active Program Requirement",
          body: "A POF is fundamentally different from a Private Non-Operating Foundation. The POF must actively conduct its own charitable programs (e.g., operating a school, clinic, research institute, or museum). Simply writing checks to other charities does not satisfy the operating test. Failure to qualify annually as an operating foundation converts it to a non-operating foundation with stricter distribution rules and donor AGI limitations.",
        },
        {
          title: "Reasonable Compensation Only",
          body: "Family members and the founder can be compensated for genuine services rendered, but compensation must be reasonable, documented, and defensible. Excessive salaries paid to family are treated as self-dealing and subject to excise taxes. An independent compensation study is advisable before setting any compensation.",
        },
        {
          title: "No Personal Benefit",
          body: "Assets of the POF must be used exclusively for charitable purposes. The founder and family cannot use foundation assets for personal benefit (e.g., travel, vehicles, art collections, or personal offices) even if they claim a charitable purpose. The IRS scrutinizes lifestyle foundations intensely.",
        },
        {
          title: "Contribution Deduction Limitations",
          body: "Cash contributions are deductible up to 60% of AGI; appreciated assets are limited to 30%. If your income varies significantly year to year, unused deductions carry forward only 5 years and can expire unused. Plan contributions around your income projections.",
        },
        {
          title: "Ongoing Governance Requirements",
          body: "A POF must maintain proper corporate governance: regular board meetings with documented minutes, a conflict-of-interest policy, and independent oversight. Courts and the IRS treat failures of governance as evidence of private inurement or self-dealing.",
        },
        {
          title: "State Law Compliance",
          body: "Most states require annual registration of charitable organizations, and some states impose their own excise taxes or additional distribution requirements. Multi-state operations add complexity and cost.",
        },
        {
          title: "Exit and Dissolution Complexity",
          body: "Winding down a POF requires distributing remaining assets to another qualified public charity or operating foundation. Assets cannot revert to the founder. Dissolution requires IRS and state approvals. Plan your exit strategy from the outset.",
        },
      ],
      nextSteps: [
        "Schedule a tax projection meeting with your Linked Accounting advisor to model the deduction against your specific AGI and projected income.",
        "Engage a nonprofit attorney experienced in private foundation law to draft Articles of Incorporation and Foundation Bylaws.",
        "File IRS Form 1023 for tax-exempt status recognition (allow 3–6 months for IRS processing).",
        "Identify and document the foundation's specific charitable mission and program activities before the first contribution.",
        "Establish a board of directors, including at least one independent member, and adopt required governance policies.",
        "Open a dedicated foundation bank and investment account, separate from personal and business accounts.",
        "Consult legal counsel regarding self-dealing rules before designating any foundation employees, contractors, or vendors.",
      ],
      disclaimer:
        "This document is for informational and educational purposes only and does not constitute legal, tax, or investment advice. Tax laws are subject to change and individual circumstances vary. The projections presented herein are hypothetical illustrations based on stated assumptions and are not guaranteed. Taxpayers should consult with qualified legal counsel and a CPA before implementing any tax strategy. IRS Circular 230 Disclosure: To the extent this communication contains tax advice, it is not intended or written to be used, and cannot be used, by any taxpayer for the purpose of avoiding penalties that may be imposed under the Internal Revenue Code.",
    },
  },
  {
    id: "crt_clt",
    name: "Charitable Remainder / Lead Trusts",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §664, §170(f)(2)",
    shortDescription:
      "CRT/CLT structures convert appreciated assets into a tax-favored income or charitable stream.",
    longDescription:
      "CRT defers gain on funding, provides an income stream to grantor, and a remainder to charity. CLT does the inverse — pays charity first, remainder to family. Both generate immediate charitable deductions.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "zero_tax_state_residency",
    name: "0% Tax State on Portfolio / Income",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: null,
    shortDescription:
      "Establish residency in a no-income-tax state (FL, TX, NV, WY, TN, SD, AK, WA) to eliminate state tax on portfolio income.",
    longDescription:
      "Requires genuine domicile change — voter registration, driver's license, time count, and severance of prior-state ties. Be wary of source-rule traps (rental real estate in old state, partnership K-1s, etc.).",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "ilit",
    name: "Irrevocable Life Insurance Trust (ILIT)",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §2042",
    shortDescription:
      "Hold life insurance in an ILIT to remove the death benefit from the taxable estate.",
    longDescription:
      "Premiums funded with annual exclusion gifts via Crummey notices. Avoids inclusion under §2042 if the insured retains no incidents of ownership. Coordinate with overall estate plan.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "tax_advantaged_accounts",
    name: "Tax-Advantaged Accounts (HSA, 529, MAGA)",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §223, §529",
    shortDescription:
      "Maximize HSA, 529, and other tax-advantaged accounts for triple- or double-tax-free growth.",
    longDescription:
      "HSA: deductible in, tax-free growth, tax-free out for medical (best account in the code). 529: tax-free growth for education, state deduction in many states. (Note: confirm 'MAGA' acronym with firm — possibly MSA / ABLE.)",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "educator_expenses",
    name: "Educator Expense Deduction",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §62(a)(2)(D)",
    shortDescription:
      "K–12 teachers can deduct up to the annual limit of classroom expenses above the line.",
    longDescription:
      "Above-the-line deduction available even to non-itemizers. Verify current-year cap (indexed). Eligible: books, supplies, software, PD courses.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "primary_residence_exclusion",
    name: "Primary Residence Sale Exclusion",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §121",
    shortDescription:
      "Exclude up to $250K ($500K MFJ) of gain on sale of a principal residence.",
    longDescription:
      "Requires 2-of-5-year ownership and use tests. Plan around partial exclusions for health/job/unforeseen moves. Careful with non-qualified use post-2009.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "tip_ot_classification",
    name: "Tip & Overtime Classification (post-OBBBA)",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "OBBBA §70201–70202",
    shortDescription:
      "Use the new federal exemptions for qualified tip income and overtime premiums correctly.",
    longDescription:
      "OBBBA introduced limited federal exclusions for tips and OT premium pay. Verify employer reporting on W-2 and proper Schedule classifications. Sunsets — check current-year status.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "plan_agi_aca",
    name: "Plan AGI to Maximize ACA Premium Credits",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §36B",
    shortDescription:
      "Manage AGI to land in the ACA premium tax credit sweet spot — large effective marginal benefit.",
    longDescription:
      "The premium tax credit phases on AGI as a % of FPL. Each $1 over a cliff can cost thousands. Use Roth conversions, retirement contributions, and HSA funding to fine-tune AGI.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "evaluate_tax_credits_individual",
    name: "Fully Evaluate Tax Credit Availability",
    categories: ["individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: null,
    shortDescription:
      "Sweep for Saver's Credit, EV credit, residential energy, dependent care, foreign tax, etc.",
    longDescription:
      "Many credits get missed because they require specific schedules. Build a checklist for each return: §25C/D residential clean energy, §30D EV, §21 dependent care, §25B retirement saver's, §901 foreign tax.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },

  // ───────────────────────────────────────────────
  // INDIVIDUAL — Reduce Rate / Defer (often combined)
  // ───────────────────────────────────────────────
  {
    id: "structured_partnership",
    name: "Structured Partnership",
    categories: ["individual"],
    goals: ["reduce_rate", "defer"],
    entityTypes: ["1040", "1065"],
    citation: "IRC §704, §707",
    shortDescription:
      "Use partnership special allocations / preferred returns to convert ordinary income to capital or defer recognition.",
    longDescription:
      "Carefully drafted partnership agreements allocate income, deductions, and gain in tax-efficient ways permitted under §704(b) and §704(c). Common in real estate and PE structures.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "ltcg_holding_period",
    name: "Hold Assets Past 365 Days (LTCG)",
    categories: ["individual"],
    goals: ["reduce_rate", "defer"],
    entityTypes: ["1040"],
    citation: "IRC §1222(3), §1(h)",
    shortDescription:
      "Hold capital assets >12 months to convert ordinary STCG (up to 37%) into LTCG (0/15/20%).",
    longDescription:
      "Largest single rate-conversion lever for individual investors. Coordinate with NIIT exposure — LTCG is still subject to 3.8% NIIT above thresholds.",
    calcType: "rateDelta",
    inputs: [
      {
        key: "amount",
        label: "Amount of gain converted to LTCG",
        type: "money",
        default: 0,
      },
    ],
    defaultEstimate: 0,
  },
  {
    id: "profits_interest_for_hces",
    name: "Profits Interest in Lieu of Full W-2 (HCEs)",
    categories: ["individual", "business"],
    goals: ["reduce_rate", "defer"],
    entityTypes: ["1040", "1065"],
    citation: "Rev. Proc. 93-27",
    shortDescription:
      "Highly compensated employees take a profits interest instead of W-2 — capital-gain treatment on appreciation, no FICA, deferral until liquidity event.",
    longDescription:
      "Available only in partnerships/LLCs taxed as partnerships. Properly structured profits interest is non-taxable on grant (Rev. Proc. 93-27 / 2001-43) and converts ordinary comp into capital gain on exit.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "flp_income_asset_shifting",
    name: "Income & Asset Shifting via FLP",
    categories: ["individual"],
    goals: ["reduce_rate"],
    entityTypes: ["1040", "1065"],
    citation: "IRC §704(e)",
    shortDescription:
      "Family Limited Partnership shifts income to lower-bracket family members (subject to kiddie tax limits) and supports valuation discounts on transfers.",
    longDescription:
      "Capital must be a material income-producing factor. Discounts for lack of marketability/control on gifted interests. Watch §2036 retained-interest traps.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "tax_loss_harvesting",
    name: "Tax Loss Harvesting",
    categories: ["individual"],
    goals: ["reduce_rate", "defer"],
    entityTypes: ["1040"],
    citation: "IRC §1091 (wash sale)",
    shortDescription:
      "Realize losses to offset gains and up to $3K of ordinary income; carry remainder forward.",
    longDescription:
      "Avoid wash-sale rule by waiting 31 days or buying a non-substantially-identical replacement. Harvest into year-end. Track lots carefully.",
    calcType: "reduceTaxableIncome",
    inputs: [
      {
        key: "amount",
        label: "Net loss harvested (offsetting gain or up to $3K ordinary)",
        type: "money",
        default: 0,
      },
    ],
    defaultEstimate: 0,
  },
  {
    id: "crypto_no_wash_sale",
    name: "Crypto Loss Harvesting (No Wash-Sale Rule)",
    categories: ["individual"],
    goals: ["reduce_rate", "defer"],
    entityTypes: ["1040"],
    citation: "IRC §1091 (does not currently apply to digital assets)",
    shortDescription:
      "Realize crypto losses and immediately repurchase — wash-sale rules do not yet apply to digital assets.",
    longDescription:
      "Treat the strategy as time-limited — Congress has repeatedly proposed extending §1091 to digital assets. Document acquisition lots; coordinate with broker reporting (post-§6045 rules).",
    calcType: "reduceTaxableIncome",
    inputs: [
      { key: "amount", label: "Crypto losses harvested", type: "money", default: 0 },
    ],
    defaultEstimate: 0,
  },
  {
    id: "roth_conversion_timing",
    name: "Roth Conversion Timing",
    categories: ["individual"],
    goals: ["reduce_rate"],
    entityTypes: ["1040"],
    citation: "IRC §408A(d)(3)",
    shortDescription:
      "Convert traditional IRA → Roth in low-income years (post-retirement, pre-RMD) to fill up lower brackets.",
    longDescription:
      "Watch IRMAA cliffs, ACA premium credit phase-outs, and state tax. Multi-year ladder typically beats a single large conversion.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "trust_k1_election",
    name: "Trust §645 / K-1 Election",
    categories: ["individual"],
    goals: ["reduce_rate"],
    entityTypes: ["1040"],
    citation: "IRC §645",
    shortDescription:
      "Elect to treat a qualified revocable trust as part of the decedent's estate, or distribute income to beneficiaries to escape compressed 1041 brackets.",
    longDescription:
      "Trust brackets compress at ~$15K — beneficiary distributions on a K-1 push income to lower individual rates. §645 election extends fiscal-year flexibility for funeral-period planning.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "passive_nonpassive_classification",
    name: "Passive vs. Non-Passive Classification Review",
    categories: ["individual"],
    goals: ["reduce_rate", "defer"],
    entityTypes: ["1040"],
    citation: "IRC §469",
    shortDescription:
      "Re-examine grouping and material participation to free up suspended passive losses or apply REPS / STR rules.",
    longDescription:
      "Test all 7 material participation tests, document hours, consider §1.469-4 grouping elections. Coordinate with REPS and short-term rental hot-asset rules.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "best_retirement_accounts",
    name: "Optimize Retirement Account Selection",
    categories: ["individual"],
    goals: ["defer"],
    entityTypes: ["1040"],
    citation: "IRC §401, §408, §408A",
    shortDescription:
      "Choose the right account mix: 401(k), Roth 401(k), IRA, Roth IRA, backdoor / mega-backdoor Roth, after-tax 401(k).",
    longDescription:
      "For HCEs the mega-backdoor (after-tax 401(k) → in-plan Roth conversion) often dwarfs the standard $23K limit. Verify plan document allows it.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "max_employer_retirement",
    name: "Max Employer Retirement Contributions",
    categories: ["individual", "business"],
    goals: ["defer"],
    entityTypes: ALL_ENTITY,
    citation: "IRC §401(a), §408(k)",
    shortDescription:
      "Maximize employer-side contributions: profit-sharing, cash-balance/DB plan stacking, SEP for sole props.",
    longDescription:
      "Combined DC/DB plans for owner-operators in their 50s can deduct $300K+. Coordinate with W-2 wages, employee coverage, and §415 limits.",
    calcType: "reduceTaxableIncome",
    inputs: [
      { key: "amount", label: "Annual contribution", type: "money", default: 0 },
    ],
    defaultEstimate: 0,
  },
  {
    id: "installment_method_individual",
    name: "Installment Method on Asset Sale",
    categories: ["individual", "real_estate", "business"],
    goals: ["defer"],
    entityTypes: ALL_ENTITY,
    citation: "IRC §453",
    shortDescription:
      "Recognize gain ratably as installment payments are received instead of all at once.",
    longDescription:
      "Watch §453A interest charge on installment obligations >$5M, depreciation recapture (§1245/1250) recognized in year of sale, and pledge rules. Not available for inventory or marketable securities.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "deferred_sales_trust",
    name: "Deferred Sales Trust",
    categories: ["individual", "real_estate"],
    goals: ["defer"],
    entityTypes: ["1040"],
    citation: "IRC §453",
    shortDescription:
      "Sell appreciated asset to a third-party trust on installment, defer gain, and invest proceeds inside the trust.",
    longDescription:
      "Promoter-driven structure that relies on §453 installment treatment with a non-related buyer. Diligence the trustee, fee load, and IRS scrutiny risk before recommending.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "investment_portfolio_analysis",
    name: "Tax-Aware Investment Portfolio Analysis",
    categories: ["individual"],
    goals: ["defer"],
    entityTypes: ["1040"],
    citation: null,
    shortDescription:
      "Asset location: ordinary-income assets in tax-deferred accounts, growth/qualified-dividend assets in taxable.",
    longDescription:
      "Bonds and REITs in IRA/401(k); index equities and muni bonds in taxable. Direct indexing for ongoing TLH. Review turnover ratios and qualified-dividend percentages.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "tax_deferred_annuities",
    name: "Tax-Deferred Annuities",
    categories: ["individual"],
    goals: ["defer"],
    entityTypes: ["1040"],
    citation: "IRC §72",
    shortDescription:
      "Non-qualified annuity defers earnings until withdrawal — earnings taxed as ordinary income on distribution.",
    longDescription:
      "Use only after maxing every other tax-advantaged option. High fees and surrender charges common. Fits a narrow set of high-income, conservative-investor clients.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },

  // ───────────────────────────────────────────────
  // REAL ESTATE
  // ───────────────────────────────────────────────
  {
    id: "stepped_up_basis",
    name: "Stepped-Up Basis at Death",
    categories: ["real_estate", "individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §1014",
    shortDescription:
      "Hold appreciated real estate until death — heirs receive a stepped-up basis, eliminating built-in gain.",
    longDescription:
      "Coordinate with community property rules (full step-up on first death), JTWROS limitations, and trust drafting (Crummey / formula clauses).",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "swap_til_you_drop",
    name: "1031 Exchange Held Until Death",
    categories: ["real_estate"],
    goals: ["eliminate"],
    entityTypes: ALL_PASS,
    citation: "IRC §1031, §1014",
    shortDescription:
      "Chain §1031 exchanges through life — held until death, deferred gain is eliminated by §1014 step-up.",
    longDescription:
      "Each §1031 carries forward the original basis. At death, basis steps up to FMV — the entire deferred gain disappears. The cornerstone of dynasty real estate planning.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "opportunity_zones",
    name: "Opportunity Zones",
    categories: ["real_estate", "individual"],
    goals: ["eliminate", "defer"],
    entityTypes: ALL_ENTITY,
    citation: "IRC §1400Z-2",
    shortDescription:
      "Defer original capital gain via QOF investment; eliminate gain on the QOF investment if held 10 years.",
    longDescription:
      "Verify QOZ map, 90% asset test for QOF, substantial improvement requirement for buildings. The 10-year exclusion is the headline benefit — the deferral has been compressed by post-2026 rules.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "conservation_easement",
    name: "Conservation Easement",
    categories: ["real_estate"],
    goals: ["eliminate"],
    entityTypes: ALL_ENTITY,
    citation: "IRC §170(h)",
    shortDescription:
      "Donate a perpetual conservation easement on real property — large charitable deduction at FMV of the easement.",
    longDescription:
      "IRS scrutiny of syndicated conservation easements is severe — only consider non-syndicated, single-property deals with rigorous appraisal and §170(h)(5) qualified organization. Listed transaction risk.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "section_1250_allocation",
    name: "Sale Proceeds Allocated to §1250",
    categories: ["real_estate"],
    goals: ["eliminate", "reduce_rate", "defer"],
    entityTypes: ALL_ENTITY,
    citation: "IRC §1250, §1245",
    shortDescription:
      "Allocate sale price between §1245 (recapture as ordinary) and §1250 (25% unrecaptured cap rate) to favor §1250.",
    longDescription:
      "Cost-seg studies often expose §1245 personal property — at sale, that gain recaptures at ordinary rates. Negotiate sale-price allocation in the contract toward real property components.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "niit_exclusion",
    name: "NIIT Exclusion Evaluation",
    categories: ["real_estate", "individual"],
    goals: ["eliminate"],
    entityTypes: ["1040"],
    citation: "IRC §1411(c)(2)",
    shortDescription:
      "Real estate professional with material participation can exclude rental income from the 3.8% NIIT.",
    longDescription:
      "Requires REPS qualification AND material participation in the rental activity (separate test). Document hours rigorously; a §1.469-9(g) grouping election is usually required.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "45L_credit",
    name: "§45L New Energy-Efficient Home Credit",
    categories: ["real_estate", "business"],
    goals: ["eliminate"],
    entityTypes: ALL_ENTITY,
    citation: "IRC §45L",
    shortDescription:
      "Credit per qualifying energy-efficient dwelling unit acquired/leased — common for builders and multifamily developers.",
    longDescription:
      "Post-IRA: $2,500 (Energy Star) or $5,000 (Zero Energy Ready) per unit. Requires DOE certification. Coordinate with §179D for commercial portion.",
    calcType: "taxCredit",
    inputs: [
      { key: "amount", label: "Total §45L credit", type: "money", default: 0 },
    ],
    defaultEstimate: 0,
  },
  {
    id: "augusta_rule",
    name: "Augusta Rule (§280A(g))",
    categories: ["real_estate", "business"],
    goals: ["eliminate"],
    entityTypes: ["1040", "1120S", "1120"],
    citation: "IRC §280A(g)",
    shortDescription:
      "Rent personal residence to your business for ≤14 days/year — deductible to business, tax-free to owner.",
    longDescription:
      "Document FMV daily rate (comparable hotel/event rates), bona fide business purpose, board minutes / meeting agenda, and 1099 not required. Limit: 14 days max (15+ taints the entire year).",
    calcType: "reduceTaxableIncome",
    inputs: [
      { key: "days", label: "Days rented (≤14)", type: "number", default: 0 },
      { key: "dailyRate", label: "FMV daily rate ($)", type: "money", default: 0 },
    ],
    defaultEstimate: 0,
  },
  {
    id: "avoid_dealer_classification",
    name: "Avoid Dealer Classification",
    categories: ["real_estate"],
    goals: ["reduce_rate"],
    entityTypes: ALL_ENTITY,
    citation: "IRC §1221(a)(1)",
    shortDescription:
      "Avoid 'dealer' status (inventory) on real estate flips — preserves capital gain treatment and §1031 eligibility.",
    longDescription:
      "Multi-factor test: frequency, intent, advertising, improvements, holding period. Use separate entities for hold vs. flip activities. Dealer property is ordinary income AND ineligible for §1031.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "grouping_elections",
    name: "§469 Grouping Elections",
    categories: ["real_estate"],
    goals: ["reduce_rate"],
    entityTypes: ALL_ENTITY,
    citation: "Treas. Reg. §1.469-4",
    shortDescription:
      "Group rentals (and rentals with active business) to meet material participation in aggregate.",
    longDescription:
      "One-time election once made — IRS challenge requires showing it doesn't reflect economic reality. §1.469-9(g) for REPS combines all rentals as one activity.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "cost_segregation",
    name: "Cost Segregation Study",
    categories: ["real_estate", "business"],
    goals: ["reduce_rate", "defer"],
    entityTypes: ALL_ENTITY,
    citation: "IRC §168, Rev. Proc. 87-56",
    shortDescription:
      "Reclassify portions of building cost from 39/27.5-year to 5/7/15-year — accelerated depreciation + bonus.",
    longDescription:
      "Engineering-based study identifies §1245 personal property and §1250 land improvements. Combine with §168(k) bonus depreciation. §481(a) catch-up available without amending prior years (Form 3115).",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "residual_soil_fertility",
    name: "Residual Soil Fertility Deduction (Agricultural Land)",
    categories: ["real_estate", "business"],
    goals: ["reduce_rate", "defer"],
    entityTypes: ALL_ENTITY,
    citation:
      "IRC §180 (active farming); §167, §168, §611 (depletion — no farming required). PLR 9211007. MSSP 3149-122.",
    shortDescription:
      "Cost-seg for farmland — allocate 10–15% of purchase price to residual soil nutrients built up by the prior owner and deduct as fertility is consumed.",
    longDescription:
      "When agricultural land is purchased, a portion of the price represents residual soil fertility (N, P, K, organic matter, micronutrients) the prior owner built up above baseline. A qualified soil-fertility study (grid sampling ~10 acres, agronomist analysis, hindcasting via fertilizer/yield history for cropland or stocking rates for ranchland) quantifies that fertility and allocates basis to it. Typical: 10–15% of land market value, averaging ~$1,700/acre. Deducted as the fertility is consumed. Under §180, an actively farming taxpayer can deduct the full amount in the year of the filing. Under §167 / §168 / §611 (no active-farming requirement — the landowner can be a passive holder), the deduction spreads over 3–7 years (typically 4-year straight-line). Sale basis is reduced dollar-for-dollar; recapture is ordinary or a §1231 mix on disposition, but resolves via step-up at death. Retroactive claims allowed up to 15 prior years. Study takes ~55–60 days. Big for Idaho / Iowa / Nebraska / Dakotas farmland deals — Boa Safra Ag is one provider. Example: $5M farmland → $3.5M fertility allocation → $3.5M ordinary deduction (§180) or spread 4 years.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "short_term_rental",
    name: "Short-Term Rental (STR) Loophole",
    categories: ["real_estate"],
    goals: ["reduce_rate"],
    entityTypes: ["1040", "1120S", "1065"],
    citation: "Treas. Reg. §1.469-1T(e)(3)(ii)",
    shortDescription:
      "STR with avg stay ≤7 days is non-rental for §469 — material participation alone produces non-passive losses.",
    longDescription:
      "Material participation tests apply (typically 100-hour or 500-hour). Combined with cost seg + bonus, STR generates massive year-1 losses against W-2 income for non-REPS taxpayers.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "section_179d",
    name: "§179D Energy-Efficient Commercial Buildings",
    categories: ["real_estate", "business"],
    goals: ["reduce_rate"],
    entityTypes: ALL_ENTITY,
    citation: "IRC §179D",
    shortDescription:
      "Deduction per square foot for energy-efficient lighting, HVAC, or building envelope upgrades.",
    longDescription:
      "Post-IRA: up to $5/sf with prevailing wage + apprenticeship. Designer of government-owned buildings can claim by allocation. Requires qualified third-party certification.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "real_estate_pro",
    name: "Real Estate Professional Status (REPS)",
    categories: ["real_estate"],
    goals: ["reduce_rate"],
    entityTypes: ["1040"],
    citation: "IRC §469(c)(7)",
    shortDescription:
      "Spouse with >750 hrs and >50% personal services in real property trades or businesses unlocks unlimited rental loss deduction.",
    longDescription:
      "Hour log critical — IRS routinely challenges. Material participation in EACH rental separately unless §1.469-9(g) grouping is filed. Coordinate with §1411 NIIT exclusion.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "depreciation_timing_481a",
    name: "Depreciation Timing & §481(a) Adjustments",
    categories: ["real_estate", "business"],
    goals: ["defer"],
    entityTypes: ALL_ENTITY,
    citation: "IRC §168, §481(a)",
    shortDescription:
      "Time bonus / §179 elections and use Form 3115 §481(a) catch-up to optimize current-year deduction.",
    longDescription:
      "Bonus phase-down: 100% (TCJA pre-2023), 80→60→40→20% schedule unless OBBBA changes. §179 has SUV cap and taxable-income limit. §481(a) catch-up sweeps prior-year missed depreciation.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "leveraged_equity_cashout",
    name: "Leveraged Equity Cash-Out Refinance",
    categories: ["real_estate"],
    goals: ["defer"],
    entityTypes: ALL_ENTITY,
    citation: null,
    shortDescription:
      "Cash-out refi pulls appreciation out tax-free as debt — defers tax until eventual sale.",
    longDescription:
      "Loan proceeds aren't income. Borrower retains property, ongoing depreciation, and interest deduction (subject to §163(j) and tracing rules). Combine with 'swap-til-you-drop' or hold-til-death.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "section_1031",
    name: "1031 Like-Kind Exchange",
    categories: ["real_estate"],
    goals: ["defer"],
    entityTypes: ALL_ENTITY,
    citation: "IRC §1031",
    shortDescription:
      "Exchange real property for like-kind real property — defer all gain into the replacement basis.",
    longDescription:
      "45-day ID, 180-day close. Qualified intermediary required. Boot (cash or relief of debt) is taxable. Real property only post-TCJA. Reverse and improvement exchanges available.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },

  // ───────────────────────────────────────────────
  // BUSINESS OWNER / SELF-EMPLOYED
  // ───────────────────────────────────────────────
  {
    id: "reclass_costs_deductible",
    name: "Reclassify Costs to Be Deductible",
    categories: ["business"],
    goals: ["eliminate"],
    entityTypes: ALL_ENTITY,
    citation: "IRC §162",
    shortDescription:
      "Sweep personal-flavored expenses for legitimate business purpose — accountable plan, home office, vehicle, travel.",
    longDescription:
      "Build a written accountable plan to reimburse owner-employee expenses tax-free. Document business purpose. Capture cell phone, home office, vehicle, books/training, business meals.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "hire_kids",
    name: "Hire Children in Your Business (LLC Preferred)",
    categories: ["business"],
    goals: ["eliminate"],
    entityTypes: ["1040", "1065"],
    citation: "IRC §3121(b)(3), §63",
    shortDescription:
      "Pay children for legitimate work — wages deductible to business, kids use standard deduction, and FICA exempt if sole prop / spousal LLC and child <18.",
    longDescription:
      "Document timesheet, FMV wage, actual work performed. FICA exemption only if business is sole prop or partnership of both parents. Child can fund Roth IRA from earned income.",
    calcType: "reduceTaxableIncome",
    inputs: [
      { key: "amount", label: "Annual wages paid", type: "money", default: 0 },
    ],
    defaultEstimate: 0,
  },
  {
    id: "college_kids_independent",
    name: "College Children Claim Themselves + Education Credits",
    categories: ["business", "individual"],
    goals: ["eliminate", "reduce_rate"],
    entityTypes: ["1040"],
    citation: "IRC §25A, §152",
    shortDescription:
      "If parents are over the AOTC phase-out, having the student claim themselves can capture the refundable AOTC.",
    longDescription:
      "Student must provide >50% of own support OR fail dependency tests. AOTC phase-out for 2025+ MFJ ~$180K. 40% refundable. Compare scenarios — lost dependency exemption value vs. gained credit.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "qsbs_1202",
    name: "Qualified Small Business Stock (§1202)",
    categories: ["business"],
    goals: ["eliminate"],
    entityTypes: ["1040", "1120"],
    citation: "IRC §1202",
    shortDescription:
      "Up to 100% exclusion of gain (greater of $10M or 10× basis) on sale of qualifying C-corp stock held >5 years.",
    longDescription:
      "Issuer must be domestic C-corp with <$50M aggregate gross assets at issuance, active business test, qualifying trade/business. Stack §1202 across family members and trusts to multiply the cap.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "nevada_trust_ccorp_sale",
    name: "Nevada / Incomplete Gift Trust (NING/DING) for C-Corp Sale",
    categories: ["business"],
    goals: ["eliminate"],
    entityTypes: ["1040", "1120"],
    citation: null,
    shortDescription:
      "Pre-sale transfer of stock to a no-state-tax-resident trust eliminates state-level capital gains tax on liquidity event.",
    longDescription:
      "Setup must precede a binding letter of intent (anticipatory assignment of income risk). Coordinate with state-of-residence challenge — CA, NY have aggressive rules attacking these structures.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "previously_disallowed_qbi",
    name: "Previously Disallowed QBI Review",
    categories: ["business"],
    goals: ["eliminate"],
    entityTypes: ALL_PASS,
    citation: "IRC §199A",
    shortDescription:
      "Re-examine prior-year QBI disallowances — wage limits, SSTB phase-out, aggregation election may have been missed.",
    longDescription:
      "OBBBA made §199A permanent. Common misses: failure to aggregate trades or businesses, missed UBIA basis, mishandled SSTB phase-out, REIT dividends not included.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "retain_divorced_spouse_owner",
    name: "Retain Divorced Spouse as Small Owner",
    categories: ["business"],
    goals: ["eliminate", "reduce_rate"],
    entityTypes: ["1120S", "1065", "1120"],
    citation: null,
    shortDescription:
      "Retain ex-spouse as a minority owner to preserve QSBS, basis, valuation, or other ownership-dependent benefits.",
    longDescription:
      "Highly fact-specific — coordinate with divorce decree, §1041 transfer rules, and any shareholder-agreement triggers. Document business purpose to avoid step-transaction recharacterization.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "avoid_max_sep",
    name: "Avoid Maximizing SEP IRA (Choose Better Plan)",
    categories: ["business"],
    goals: ["eliminate"],
    entityTypes: ["1040", "1120S", "1065"],
    citation: "IRC §408(k), §401(a)",
    shortDescription:
      "SEP locks owner-pro-rata to employees — Solo 401(k), SIMPLE, or Safe Harbor 401(k) often gives owner more deferral with less employee cost.",
    longDescription:
      "SEP requires equal % to all eligible employees. Solo 401(k) only works with no full-time employees other than spouse. Cash-balance + 401(k) stack is strongest for owners 50+.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "entity_classification_se_tax",
    name: "Entity Classification & SE Tax Optimization",
    categories: ["business"],
    goals: ["eliminate"],
    entityTypes: ["1040", "1120S", "1065"],
    citation: "IRC §1402, Rev. Rul. 59-221",
    shortDescription:
      "S-corp election typically eliminates SE tax on distributions above reasonable comp; LLC structures can split SE-taxable vs. non-SE-taxable income.",
    longDescription:
      "Reasonable compensation must be substantiated (RC Reports, BLS, comparable studies). Limited-partner-like distributive shares can escape SE tax under §1402(a)(13) (Renkemeyer caution).",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "reduce_office_comp",
    name: "Reduce Owner Compensation (Within Reason)",
    categories: ["business"],
    goals: ["eliminate"],
    entityTypes: ["1120S"],
    citation: "Rev. Rul. 74-44, §1402",
    shortDescription:
      "Right-size S-corp owner W-2 to reasonable comp — distributions above that escape FICA / SE tax.",
    longDescription:
      "Document with reasonable comp study. Too-low salaries are an IRS exam priority. Coordinate with §199A wage limit (lower W-2 may shrink QBI deduction for SSTBs).",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "evaluate_tax_credits_business",
    name: "Fully Evaluate Business Tax Credits",
    categories: ["business"],
    goals: ["eliminate"],
    entityTypes: ALL_ENTITY,
    citation: null,
    shortDescription:
      "Sweep R&D (§41), WOTC (§51), §45 energy, §44 disabled access, §45F dependent care, employer-provided child care, etc.",
    longDescription:
      "R&D often the largest. WOTC requires pre-screening before hire. Energy credits need careful documentation post-IRA. Build a credit checklist into the year-end planning template.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "state_nexus_analysis",
    name: "Full State Nexus Analysis",
    categories: ["business"],
    goals: ["eliminate"],
    entityTypes: ALL_ENTITY,
    citation: "Wayfair (2018)",
    shortDescription:
      "Identify and resolve unfiled state nexus exposure to avoid penalty / interest accumulation; consider VDAs.",
    longDescription:
      "Post-Wayfair economic nexus ($100K or 200 transactions in many states) and physical-presence nexus both apply. Voluntary Disclosure Agreements typically waive penalties + cap lookback.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "cash_vs_accrual_timing",
    name: "Cash vs. Accrual Timing",
    categories: ["business"],
    goals: ["reduce_rate", "defer"],
    entityTypes: ALL_ENTITY,
    citation: "IRC §448, §451, §461",
    shortDescription:
      "Choose cash or accrual method (or change via Form 3115) for optimal year-end timing.",
    longDescription:
      "OBBBA expanded cash-method eligibility ($31M gross receipts test). Cash method allows year-end deferral (delay invoicing, prepay deductible expenses). §461(h) economic performance still applies.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "section_831b_captive",
    name: "Section 831(b) Micro-Captive Insurance",
    categories: ["business"],
    goals: ["reduce_rate", "defer"],
    entityTypes: ["1120S", "1065", "1120"],
    citation: "IRC §831(b)",
    shortDescription:
      "Form a captive insurance company — premium deduction to operating co, premiums received tax-free at captive.",
    longDescription:
      "IRS listed transaction status for abusive structures. Must be genuine insurance: risk shifting + risk distribution + insurance risk + commonly accepted notions. Annual premium cap (~$2.85M indexed). Reportable transaction disclosures required.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "sale_allocation",
    name: "Optimize Sale Price Allocation (§1060)",
    categories: ["business"],
    goals: ["reduce_rate", "defer"],
    entityTypes: ALL_ENTITY,
    citation: "IRC §1060",
    shortDescription:
      "Negotiate sale price allocation in asset deals to maximize capital-gain (goodwill, §1250) and minimize ordinary recapture.",
    longDescription:
      "Buyer/seller often have opposite incentives — buyer wants short-life depreciable, seller wants long-life capital. Form 8594 must match. Personal goodwill allocation can shift gain off corporate level for C-corps.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "like_kind_business_assets",
    name: "Like-Kind Treatment of Business Assets",
    categories: ["business"],
    goals: ["defer"],
    entityTypes: ALL_ENTITY,
    citation: "IRC §1031 (real property only post-TCJA)",
    shortDescription:
      "Real-property §1031 still defers gain; for personal property, evaluate trade-in / installment alternatives.",
    longDescription:
      "Post-TCJA personal property no longer eligible — but real estate held by the business still qualifies. For equipment trade-ins, §1031 unavailable; §1245 recapture is ordinary at sale.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  // ─── Cash-basis year-end timing shifts ───────────────────────────────
  // Two classic year-end plays for cash-basis clients. Partner enters the
  // dollar amount as "Manual reduction to TI" on the strategy; it flows to
  // the entity's taxable income and can be toggled per scenario in the
  // Y/M/Out chips.
  {
    id: "delay_year_end_invoicing",
    name: "Delay Year-End Invoicing (Cash Basis)",
    categories: ["business"],
    goals: ["defer"],
    entityTypes: ["1120S", "1065", "sched_c"],
    citation: "IRC §451 / §461 timing (cash method)",
    shortDescription:
      "Push year-end invoicing into January so revenue is recognized (received) next year — cash-basis entities only.",
    longDescription:
      "For clients on the cash method for tax, income is recognized when received. Delaying invoicing of $X of December work into January defers roughly $X of income into next year. Only viable for cash-basis entities (not accrual). Watch: client must actually not receive the cash before 12/31 — constructive receipt applies. Best paired with client cash-flow needs. Enter the amount delayed as the Manual Reduction to TI; the tool subtracts it from this year's taxable income at the entity level.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
  {
    id: "prepay_year_end_bills",
    name: "Prepay Year-End Bills (Cash Basis)",
    categories: ["business"],
    goals: ["defer"],
    entityTypes: ["1120S", "1065", "sched_c"],
    citation: "IRC §461 / §263 timing (cash method, 12-month rule)",
    shortDescription:
      "Pay January bills in December to accelerate deductions — cash-basis entities only.",
    longDescription:
      "Cash-basis entities deduct expenses when paid. Prepaying $X of January bills (rent, utilities, insurance, supplies) in December pulls the deduction into this year. §263 12-month rule allows deducting prepaid expenses if the benefit doesn't extend past 12 months from payment. Watch: recurring nature required, not just materials for the following year. Enter the amount prepaid as the Manual Reduction to TI; the tool subtracts it from this year's taxable income at the entity level.",
    calcType: "manual",
    inputs: [],
    defaultEstimate: 0,
  },
];

// Merge timing data into each strategy
for (const s of STRATEGIES) {
  s.timing = TIMING[s.id] || { kind: "ongoing", note: "" };
}

// ─────────────────────────────────────────────────────────────────────
// Complexity tier per strategy.
// 1 — Foundational: most clients can implement; minimal coordination
// 2 — Intermediate: entity setup or annual coordination required
// 3 — Advanced:    complex structures, specialized counsel needed
// ─────────────────────────────────────────────────────────────────────
const COMPLEXITY = {
  // Tier 1 — Foundational
  muni_bonds: 1,
  donate_appreciated_capital_assets: 1,
  deduction_stacking_schedule_a: 1,
  tax_advantaged_accounts: 1,
  educator_expenses: 1,
  primary_residence_exclusion: 1,
  tip_ot_classification: 1,
  plan_agi_aca: 1,
  evaluate_tax_credits_individual: 1,
  ltcg_holding_period: 1,
  tax_loss_harvesting: 1,
  crypto_no_wash_sale: 1,
  best_retirement_accounts: 1,
  max_employer_retirement: 1,
  hire_kids: 1,
  college_kids_independent: 1,
  avoid_max_sep: 1,
  augusta_rule: 1,
  evaluate_tax_credits_business: 1,
  reclass_costs_deductible: 1,
  max_non_retirement_employer_benefits: 1,
  state_nexus_analysis: 1,

  // Tier 2 — Intermediate
  roth_conversion_timing: 2,
  cash_vs_accrual_timing: 2,
  sale_allocation: 2,
  like_kind_business_assets: 2,
  entity_classification_se_tax: 2,
  reduce_office_comp: 2,
  previously_disallowed_qbi: 2,
  depreciation_timing_481a: 2,
  leveraged_equity_cashout: 2,
  grouping_elections: 2,
  avoid_dealer_classification: 2,
  niit_exclusion: 2,
  "45L_credit": 2,
  section_179d: 2,
  section_1031: 2,
  cost_segregation: 2,
  investment_portfolio_analysis: 2,
  passive_nonpassive_classification: 2,
  retain_divorced_spouse_owner: 2,
  life_insurance_death_benefit: 2,
  structured_partnership: 2,
  zero_tax_state_residency: 2,
  installment_method_individual: 2,
  section_1250_allocation: 2,
  stepped_up_basis: 2,
  foreign_earned_income_exclusion: 2,

  // Tier 3 — Advanced
  shift_to_idgt: 3,
  ilit: 3,
  crt_clt: 3,
  tax_deferred_annuities: 3,
  deferred_sales_trust: 3,
  nevada_trust_ccorp_sale: 3,
  qsbs_1202: 3,
  section_831b_captive: 3,
  conservation_easement: 3,
  real_estate_pro: 3,
  short_term_rental: 3,
  swap_til_you_drop: 3,
  opportunity_zones: 3,
  trust_k1_election: 3,
  profits_interest_for_hces: 3,
  flp_income_asset_shifting: 3,
  private_operating_foundation: 3,
};

for (const s of STRATEGIES) {
  s.complexity = COMPLEXITY[s.id] || 2; // default intermediate
}

export function getStrategyComplexity(strategyId) {
  return COMPLEXITY[strategyId] || 2;
}

export const TIER_META = {
  1: {
    label: "Foundational",
    description:
      "Core planning the firm runs annually — minimal coordination, broad applicability.",
    color: "emerald",
  },
  2: {
    label: "Intermediate",
    description:
      "Entity setup or annual coordination required — typical for active business owners.",
    color: "sky",
  },
  3: {
    label: "Advanced",
    description:
      "Complex structures requiring specialized counsel and rigorous documentation — premium engagement scope.",
    color: "amber",
  },
};

// ─────────────────────────────────────────────────────────────────────
// Rich client-facing briefs.
//
// Each entry is a structured document the planner can pop out from the
// strategy row to preview, download, or email. Renderer expects sections
// of these types:
//   heading      { type, text }
//   paragraph    { type, text }
//   table        { type, headers, rows, theme }
//   callout      { type, variant ("highlight"|"warn"|"info"|"success"), title, body }
//   threeColCompare { type, columns: [{ theme, title, items }] }
//   twoColCompare   { type, left, right } — each has { theme, title, items }
//   bullets      { type, items }
// ─────────────────────────────────────────────────────────────────────
export const CLIENT_BRIEFS = {
  short_term_rental: {
    title: "Short-Term Rental Tax Strategy Brief",
    subtitle:
      "IRC §469 Passive Activity Loss Rules & Material Participation — Seven-Part Test Analysis",
    intro:
      "Under IRC §469, rental activities are generally treated as passive — meaning losses may only offset passive income. However, short-term rentals (average guest stay of 7 days or less) are reclassified as a trade or business under Reg. 1.469-1T(e)(3), making the material participation rules under Treas. Reg. 1.469-5T applicable. An owner who materially participates may deduct losses against active income. Material participation is satisfied by meeting any one of the seven tests below.",
    sections: [
      {
        type: "heading",
        text: "The Seven Material Participation Tests — IRC §469 / Treas. Reg. 1.469-5T",
      },
      {
        type: "table",
        headers: ["#", "Name", "Requirement"],
        rows: [
          ["1", "500+ Hours", "You participate more than 500 hours during the tax year."],
          [
            "2",
            "Substantially All Participation",
            "Your participation constitutes substantially all participation in the activity by all individuals.",
          ],
          [
            "3 ★",
            "100+ Hours & More Than Anyone Else (PRIMARY TEST FOR STR OWNERS)",
            "You participate more than 100 hours AND no other individual participates more than you. Most commonly applicable test for short-term rental owners who engage a management company.",
          ],
          [
            "4",
            "Significant Participation Activities (SPA) Aggregate",
            "The activity is a SPA (100+ hours) and your total hours across all SPAs exceed 500 hours combined.",
          ],
          [
            "5",
            "Material Participation — 5 of Prior 10 Years",
            "You materially participated in the activity in any 5 of the 10 immediately preceding tax years.",
          ],
          [
            "6",
            "Personal Service Activity — 3 Prior Years",
            "The activity is a personal service activity and you materially participated in any 3 prior tax years.",
          ],
          [
            "7",
            "Facts & Circumstances",
            "You participate on a regular, continuous, and substantial basis (generally 100+ hours with no paid manager handling day-to-day operations).",
          ],
        ],
        theme: "navy",
      },
      {
        type: "heading",
        text: "What Counts: Operational Hours vs. Investor Hours",
      },
      {
        type: "paragraph",
        text: "The IRS and Tax Court scrutinize the nature of hours, not just the count. Only operational hours accumulate toward the 100-hour threshold. Investor-level activities do not count regardless of time spent.",
      },
      {
        type: "twoColCompare",
        left: {
          theme: "green",
          title: "✔ OPERATIONAL HOURS — Count Toward the 100-Hour Test",
          items: [
            "Adjusting nightly rates / pricing on booking platforms",
            "Responding to guest pre-booking inquiries personally",
            "Sending check-in instructions & welcome messages to guests",
            "Handling guest complaints or issues during stays",
            "Writing responses to guest reviews on all platforms",
            "Personally inspecting the property after checkouts",
            "Soliciting and comparing vendor bids for repairs",
            "Authorizing and supervising specific repair decisions",
            "Approving capital expenditures and directing how work is scoped",
            "Sourcing and purchasing furniture, linens, or supplies",
            "Updating listing descriptions, photos, and amenity tags",
            "Managing multi-platform listing distribution decisions",
            "Updating house rules and policies on listings",
            "Negotiating management company contract terms",
            "Interviewing and selecting cleaning or maintenance vendors",
            "Filing / renewing STR permits and lodging tax remittances",
            "Monitoring STR ordinance changes affecting the property",
            "Researching and deciding on new amenities (hot tub, EV charger)",
            "Conducting seasonal walkthroughs to assess competitiveness",
            "Analyzing guest review trends and directing operational changes",
            "Approving and reviewing the annual operating budget with active input",
            "Meeting with CPA or tax advisor on property-specific tax decisions",
          ],
        },
        right: {
          theme: "red",
          title: "✗ INVESTOR HOURS — Do NOT Count Toward Any Test",
          items: [
            "Passively reading monthly management reports without follow-up action",
            "Monitoring account balances with no resulting decision or direction",
            "Attending investor calls or partnership meetings as a passive participant",
            "Reviewing financial statements without initiating any operational response",
            "General research on STR market trends not tied to a specific property decision",
            "Passively reviewing insurance renewal summaries without comparing or deciding",
          ],
        },
      },
      {
        type: "callout",
        variant: "highlight",
        title: "■ KEY CONSIDERATION: MANAGEMENT COMPANY HOUR DISTRIBUTION",
        body: "Under Test 3, the owner's hours must exceed those of any single individual — including employees of an outsourced management company. A management company with multiple employees, where no single employee organically spends more than 100 hours managing the specific property in question, satisfies this requirement naturally. For example, a management company may distribute work across a reservations team, maintenance coordinator, guest communications staff, and a regional supervisor — with no one employee dedicated solely to one property. This organic distribution is consistent with how professionally operated STR management businesses function across their entire client base. Critical distinction: the hour distribution must reflect the management company's genuine business model — not an arrangement manufactured specifically to satisfy the tax test. Evidence of organic distribution includes the management company's standard operating procedures, staffing models applied consistently across all clients, and internal time records.",
      },
      {
        type: "callout",
        variant: "info",
        title: "Documentation is Non-Negotiable",
        body: "Maintain a contemporaneous time log recording the date, duration, specific task performed, and the decision or outcome for every operational hour. Logs reconstructed at year-end carry significantly less weight in an IRS examination than real-time records. The owner's log and the management company's internal staffing records together form the complete evidentiary picture under Test 3.",
      },
      {
        type: "twoColCompare",
        left: {
          theme: "purple",
          title: "■ ACQUISITION WORK — PRE-ACTIVITY HOURS (Do Not Count)",
          body: "Hours spent acquiring a property occur before the activity exists under §469 and do not count toward any material participation test. The following are pre-activity and are excluded regardless of time invested:",
          items: [
            "Searching for and evaluating properties to purchase",
            "Negotiating the purchase contract",
            "Working with a lender — loan applications, underwriting, rate negotiations",
            "Due diligence — inspections, title review, HOA review, environmental checks",
            "Attending closing and executing purchase documents",
          ],
          footer:
            "The operational hour clock begins once the property is available for rental and operational setup commences — typically when furnishing, listing creation, permitting, and vendor selection begin post-acquisition.",
        },
        right: {
          theme: "orange",
          title: "■ Q4 ACQUISITION RISK — THE CALENDAR YEAR CONSTRAINT",
          body: "Test 3 requires 100+ operational hours accumulated within the same calendar year as the loss claimed. A property acquired in October, November, or December creates a structural problem: with only weeks remaining in the tax year, reaching 100 genuine operational hours is extremely difficult. This problem is compounded when a management company is engaged from day one — the owner's operational hours are reduced further while the management company's employees begin accumulating their own hours immediately. Test 2 (Substantially All) becomes the only realistic alternative — but this requires that the owner's participation constitute substantially all participation by anyone, which is nearly impossible to satisfy when a management company is already active on the property.",
          footer:
            "Planning implication: Properties acquired in Q4 should either defer the management company engagement to the following year, defer loss recognition to year two, or acknowledge that material participation cannot be established for the partial acquisition year.",
        },
      },
    ],
  },

  real_estate_pro: {
    title: "Real Estate Professional Status & Grouping Elections",
    subtitle:
      "IRC §469(c)(7) — Converting Passive Real Estate Losses to Active Deductions",
    intro:
      "Under IRC §469, losses from rental real estate are generally passive and may only offset passive income. However, a taxpayer who qualifies as a Real Estate Professional (REP) under §469(c)(7) escapes this per se passive treatment — rental losses become potentially deductible against ordinary income, including W-2 wages, business income, and other active income. Qualification requires meeting two threshold tests and then separately satisfying the material participation rules for each rental activity (or a grouped set of activities).",
    sections: [
      {
        type: "heading",
        text: "STEP 1 — QUALIFYING AS A REAL ESTATE PROFESSIONAL (IRC §469(c)(7))",
      },
      {
        type: "table",
        headers: ["Test", "Requirement", "Key Notes"],
        rows: [
          [
            "750-Hour Threshold",
            "More than 750 hours of services performed in real property trades or businesses in which the taxpayer materially participates during the tax year.",
            "Hours must be in qualifying real property trades or businesses — not general investment activity. Includes development, construction, acquisition, rental, management, leasing, and brokerage.",
          ],
          [
            "More-Than-Half Test",
            "The taxpayer's personal services in real property trades or businesses must exceed 50% of total personal services performed in all trades or businesses during the year.",
            "A taxpayer with a W-2 job of 2,000+ hours per year will almost never satisfy this test personally. A non-employed spouse, however, can — their real estate hours need only exceed their own total work hours, which may be exclusively real estate.",
          ],
        ],
        theme: "navy",
      },
      {
        type: "callout",
        variant: "highlight",
        title: "★ THE NON-EMPLOYED SPOUSE STRATEGY — A POWERFUL PLANNING OPPORTUNITY",
        body: "A common and highly effective planning scenario involves a married couple filing jointly where one spouse holds a full-time W-2 position (typically 2,000+ hours annually) and the other spouse does not hold outside employment. The non-employed spouse can qualify as a Real Estate Professional independently if their real estate hours exceed 750 and represent more than half of their total personal service hours — a threshold easily met when real estate is their primary occupation. Critical rule: REP status is determined on an individual basis — the employed spouse's W-2 hours do not count against the non-employed spouse's more-than-half test. Once the non-employed spouse achieves REP status, the couple's jointly-owned rental losses may be deducted against the household's combined income on the joint return, including the employed spouse's W-2 wages — provided material participation is also satisfied at the activity level (see Step 2 below). The non-employed spouse must personally log qualifying real estate hours; hours cannot be borrowed from the employed spouse to meet the REP threshold.",
      },
      {
        type: "heading",
        text: "STEP 2 — MATERIAL PARTICIPATION IN EACH RENTAL ACTIVITY (Treas. Reg. 1.469-5T)",
      },
      {
        type: "paragraph",
        text: "REP status alone does not make rental losses deductible. The taxpayer must also materially participate in each rental activity (or grouped activity — see Step 3). Material participation is established by satisfying any one of the seven tests below.",
      },
      {
        type: "table",
        headers: ["#", "Test Name", "Requirement", "Flag"],
        rows: [
          [
            "1",
            "750+ Hours (REP Adjusted)",
            "As a REP, participation in real property trades or businesses must exceed 750 hours. This replaces the standard 500-hour test for purposes of the REP qualification.",
            "★ REP-SPECIFIC",
          ],
          [
            "2",
            "Substantially All Participation",
            "Your participation constitutes substantially all participation in the activity by all individuals, including non-owners such as management company employees.",
            "",
          ],
          [
            "3",
            "100+ Hours & More Than Anyone Else",
            "You participate more than 100 hours AND no other individual participates more than you. See companion STR brief for detailed analysis.",
            "★ KEY STR TEST",
          ],
          [
            "4",
            "Significant Participation Activities — 500-Hour Aggregate",
            "The activity is a significant participation activity (100+ hours) and your combined hours across all such activities exceed 500 hours for the year.",
            "",
          ],
          [
            "5",
            "5 of Prior 10 Years",
            "You materially participated in the activity in any 5 of the 10 immediately preceding tax years.",
            "",
          ],
          [
            "6",
            "Personal Service Activity — Any 3 Prior Years",
            "The activity is a personal service activity and you materially participated in any 3 prior years.",
            "",
          ],
          [
            "7",
            "Facts & Circumstances",
            "You participate on a regular, continuous, and substantial basis (generally 100+ hours with no paid manager handling day-to-day operations).",
            "",
          ],
        ],
        theme: "navy",
      },
      {
        type: "heading",
        text: "STEP 3 — THE GROUPING ELECTION (Treas. Reg. 1.469-4)",
      },
      {
        type: "paragraph",
        text: "Without a grouping election, a REP must satisfy material participation separately for each rental property. With multiple properties this is burdensome and often impossible. The grouping election allows a REP to treat all rental activities as a single activity, aggregating hours across the entire portfolio to satisfy one material participation test collectively.",
      },
      {
        type: "threeColCompare",
        columns: [
          {
            theme: "red",
            title: "WITHOUT Grouping Election",
            items: [
              "Must satisfy material participation for each property individually",
              "A property where the owner logs 60 hours generates passive losses only",
              "Losses from under-participated properties are suspended",
              "With 5 properties, owner may need 500+ total hours to cover all",
              "Practically forces the owner to be deeply involved in every asset",
            ],
          },
          {
            theme: "green",
            title: "WITH Grouping Election",
            items: [
              "All rental properties treated as one activity",
              "Hours across all properties aggregate toward one test",
              "300 hours across 5 properties satisfies Test 3 if no single outside person exceeds that level",
              "Losses from the entire portfolio offset active income once any one test is met",
              "Dramatically reduces documentation burden across properties",
            ],
          },
          {
            theme: "navy",
            title: "Election Mechanics",
            items: [
              "Election made on a timely-filed tax return (including extensions)",
              "Disclosed on the return in the year first made",
              "Generally binding in all future years",
              "New properties added to the group in the year acquired",
              "Regrouping allowed only upon IRS-permitted material change in facts",
              "Separate LLC ownership does not prevent grouping",
            ],
          },
        ],
      },
      {
        type: "twoColCompare",
        left: {
          theme: "purple",
          title: "■ SUSPENDED LOSSES — CARRIED FORWARD, NOT LOST",
          body: "Losses that remain passive in a given year because material participation is not satisfied are suspended — not lost. They carry forward indefinitely and are released in the first year the taxpayer either (a) satisfies material participation in that activity, or (b) disposes of the activity in a fully taxable transaction. For a REP with a grouped election, suspended losses from prior years are released when the grouped activity is disposed of. This makes suspended loss tracking an important part of long-term real estate tax planning.",
        },
        right: {
          theme: "orange",
          title: "■ KEY PITFALLS TO AVOID",
          items: [
            "1. No REP status for the employed spouse alone. A spouse working 2,000 W-2 hours cannot satisfy the more-than-half test unless real estate hours also exceed 2,000 — extremely rare.",
            "2. Hours must be in qualifying activities. Time spent as a passive investor — reading reports, attending high-level meetings, reviewing statements — does not count toward the 750-hour or material participation thresholds.",
            "3. Contemporaneous records are essential. The IRS audits REP claims aggressively. A daily time log with specific tasks and decisions is required. Reconstructed logs carry minimal weight.",
            "4. Grouping is not automatic. The election must be affirmatively made and disclosed on the return. Failure to elect means property-by-property analysis applies by default.",
          ],
        },
      },
    ],
  },
};

// ─── Additional briefs sourced from the firm's strategy guide DOCX files ──
// (Linked Real Estate / Individual / Business Tax Strategies guides)
//
// These use the same renderer as the STR/REPS PDFs but are simpler:
// intro + paragraph sections + occasional highlight callouts. Each brief
// preserves the firm's client-facing voice and adds a Planning Notes
// closer where the source content warrants emphasis.

const SECONDARY_BRIEFS = {
  // ───── Real Estate ─────
  section_1031: {
    title: "§1031 Like-Kind Exchange",
    subtitle: "IRC §1031 — Deferring Capital Gains on Investment Real Estate",
    intro:
      "When you sell investment real property, you would normally owe capital gains tax on the appreciation. A §1031 exchange allows you to defer that entire tax bill by reinvesting the proceeds into a qualifying replacement property within strict IRS timelines — 45 days to identify and 180 days to close. There is no upper limit on the deferred gain.",
    sections: [
      { type: "heading", text: "Indefinite Rollover, Permanent Elimination at Death" },
      {
        type: "paragraph",
        text: "Properly executed, investors can roll gains forward indefinitely. And if held until death, heirs receive a stepped-up basis under §1014 — effectively eliminating the deferred gain permanently. This is the cornerstone of dynasty-style real estate planning, particularly for clients who intend to never personally consume the equity but instead pass it to the next generation.",
      },
      {
        type: "callout",
        variant: "warn",
        title: "■ Timing Discipline Is Non-Negotiable",
        body: "45 days to identify replacement property and 180 days to close. Both timelines run from the date the relinquished property closes — no extensions. A qualified intermediary must be engaged before closing; the seller cannot constructively receive the proceeds. Missing either timeline collapses the exchange and triggers full gain recognition.",
      },
    ],
  },

  opportunity_zones: {
    title: "Opportunity Zone Investments",
    subtitle: "IRC §1400Z-2 — A Three-Layer Tax Incentive for Capital Gains",
    intro:
      "Opportunity Zones are designated low-income census tracts where the IRS provides a three-layer incentive for investing capital gains: deferral of the original gain, a partial reduction of the deferred gain after a holding period, and complete exclusion of all appreciation in the Opportunity Zone investment if held 10+ years.",
    sections: [
      { type: "heading", text: "Best Fit" },
      {
        type: "paragraph",
        text: "Ideal for investors with large short-term or long-term capital gains looking for both tax shelter and long-horizon growth in developing real estate markets. The investor reinvests the gain into a Qualified Opportunity Fund within 180 days of the original gain recognition. The original gain defers until the earlier of the QOF disposition or December 31, 2026 (statutory).",
      },
      {
        type: "heading",
        text: "The 10-Year Exclusion",
      },
      {
        type: "paragraph",
        text: "The most powerful benefit: if the QOF investment itself is held for at least 10 years, all appreciation inside the QOF is permanently excluded from federal capital gains tax. This makes Opportunity Zones uniquely effective for clients with a long planning horizon and meaningful unrealized gains looking to redeploy.",
      },
    ],
  },

  cost_segregation: {
    title: "Cost Segregation Study",
    subtitle: "IRC §168 — Accelerated Depreciation on Real Property Components",
    intro:
      "When you acquire or construct a commercial or residential rental property, the IRS default is to depreciate the entire building over 27.5 or 39 years. A cost segregation study, conducted by an engineering firm, identifies components of the building — flooring, fixtures, electrical systems, land improvements, and more — that qualify for 5-, 7-, or 15-year depreciation schedules instead.",
    sections: [
      { type: "heading", text: "Front-Loading the Deduction" },
      {
        type: "paragraph",
        text: "Combined with bonus depreciation under §168(k), this strategy front-loads massive deductions into the first years of ownership. On a $1M property, it is common to reclassify $200,000–$350,000 of basis into accelerated schedules. The economic value is the time value of the tax savings — claiming deductions today rather than over four decades.",
      },
      {
        type: "callout",
        variant: "info",
        title: "■ §481(a) Catch-Up — Even for Older Properties",
        body: "A cost segregation study can be performed on properties owned for many years prior. A §481(a) adjustment (filed via Form 3115) sweeps the missed accelerated depreciation into the current year as a single catch-up deduction — no amended returns required. This makes cost segregation a powerful planning lever even when the property was placed in service years ago.",
      },
    ],
  },

  residual_soil_fertility: {
    title: "Residual Soil Fertility Deduction",
    subtitle:
      "IRC §180 / §167 / §168 / §611 — Cost Segregation for Agricultural Land",
    intro:
      "When you purchase agricultural land, part of what you paid for is residual soil fertility built up by the prior owner — years of accumulated nitrogen, phosphorus, potassium, organic matter, and micronutrients that sit above the region's natural baseline and directly support your ability to grow crops or feed cattle. A qualified soil-fertility study identifies and quantifies that residual fertility, and the tax code allows you to deduct that portion of your land basis as the fertility is consumed by your operation. Think of it as a cost segregation study for farmland.",
    sections: [
      { type: "heading", text: "How the Deduction Works" },
      {
        type: "paragraph",
        text: "A qualified agronomist performs grid soil sampling (typically 10-acre grids), analyzes nutrient concentrations against baseline, and reviews fertilizer application history for cropland — or stocking rates for ranchland — to produce a defensible study establishing measurable residual fertility above baseline. That portion of the land basis is then deductible as the fertility is consumed.",
      },
      {
        type: "paragraph",
        text: "Under IRC §180, a taxpayer actively engaged in farming or ranching can typically claim the entire residual fertility deduction in the year the study is completed. Under IRC §167, §168, and §611 — which do NOT require the landowner to be actively farming — the deduction spreads across 3 to 7 years, with a 4-year straight-line schedule being the common default.",
      },
      {
        type: "callout",
        variant: "highlight",
        title: "★ Typical Result: 10–15% of Land Value",
        body: "Studies typically allocate 10–15% of land market value to residual fertility, averaging roughly $1,700 per acre. On a $5 million farmland purchase, that could translate to $500,000–$750,000 of ordinary deductions — potentially the entire amount deductible in Year 1 for an actively-farming owner, or spread across 3–7 years for a passive landholder.",
      },
      { type: "heading", text: "Retroactive Claims — Up to 15 Prior Years" },
      {
        type: "paragraph",
        text: "You do NOT have to have purchased the land recently. Landowners can retroactively claim these deductions for up to 15 prior tax years without amending original returns. A §481(a) adjustment filed via Form 3115 sweeps the missed deductions into the current year as a single catch-up, which can produce a substantial one-time deduction against current-year income.",
      },
      { type: "heading", text: "Recapture and Basis on Sale" },
      {
        type: "paragraph",
        text: "The fertility deduction reduces the land's tax basis dollar-for-dollar. When the land is eventually sold, the reduced basis increases the taxable gain, which is recaptured as a mix of ordinary income and §1231 (capital) gain depending on the specific asset treatment. The rate arbitrage — an ordinary-rate deduction today (up to 37%) against a partially capital-rate recapture later (as low as 20% + 3.8% NIIT) — is real value even if the property is sold.",
      },
      {
        type: "callout",
        variant: "highlight",
        title: "★ Held to Death — The Deduction Is Permanently Free",
        body: "If the land is held until the owner's death, heirs receive a fully stepped-up basis under §1014. The residual fertility deduction taken during life is never recaptured — it becomes permanent tax elimination rather than deferral. This is a particularly compelling planning play for legacy farming and ranching operations passing to the next generation.",
      },
      { type: "heading", text: "Study Timeline and Next Steps" },
      {
        type: "callout",
        variant: "info",
        title: "■ Plan on 55–60 Days from Engagement to Report",
        body: "A soil-fertility study typically takes 55–60 days from signed engagement to a final defensible report. Plan accordingly ahead of return-filing deadlines. Providers like Boa Safra Ag specialize in these studies and operate nationwide. We can coordinate the engagement, review the report before it lands in your file, and integrate the deduction into your return.",
      },
    ],
  },

  section_1250_allocation: {
    title: "Sale Proceeds Allocated to §1250 Property",
    subtitle: "IRC §1250 vs §1245 — Capital Treatment on Property Sales",
    intro:
      "When selling a depreciated real property, a portion of the gain is taxed as §1250 unrecaptured depreciation at rates up to 25% — higher than standard long-term capital gains rates for some taxpayers. Structuring the purchase agreement to properly allocate value across land, building, and personal property, and planning which components have been depreciated and at what rates, can meaningfully reduce the unrecaptured §1250 recapture tax.",
    sections: [
      { type: "heading", text: "Why This Matters After Cost Segregation" },
      {
        type: "paragraph",
        text: "This analysis is particularly important in cost-segregated properties. The personal property reclassified to short-life schedules (§1245) recaptures at ordinary rates on sale — up to 37%. Carefully negotiated sale price allocations between §1245 personal property and §1250 real property, documented in the purchase agreement and on Form 8594, can shift basis from high-recapture-rate components to capital-gain components.",
      },
    ],
  },

  // ───── Individual ─────
  muni_bonds: {
    title: "Municipal Bond Income",
    subtitle: "IRC §103 — Federally and Often State Tax-Free Interest",
    intro:
      "Interest earned on bonds issued by state and local governments is generally excluded from federal income tax — and often from state income tax if you reside in the issuing state. For taxpayers in the 32% bracket or higher, the after-tax yield on municipal bonds can exceed that of comparable taxable bonds.",
    sections: [
      { type: "heading", text: "A Durable Shelter" },
      {
        type: "paragraph",
        text: "Including a strategic allocation of munis in your investment portfolio is one of the simplest and most durable ways to permanently shelter investment income from taxation. Unlike strategies that require careful timing or specific transactions, this is a standing portfolio decision that compounds quietly in the background.",
      },
    ],
  },

  donate_appreciated_capital_assets: {
    title: "Donating Appreciated Capital Assets",
    subtitle: "IRC §170(e) — Fair Market Value Deduction, No Capital Gains",
    intro:
      "When you donate appreciated stock, real estate, or other capital assets held more than one year directly to a qualified charity, you receive a fair market value charitable deduction and completely avoid the capital gains tax on the built-in appreciation.",
    sections: [
      { type: "heading", text: "More Tax-Efficient Than Cash" },
      {
        type: "paragraph",
        text: "This is significantly more tax-efficient than selling the asset, paying the gain, and donating the after-tax cash. For an asset with a low cost basis, the combined tax benefit — charitable deduction plus avoided capital gain — can exceed the asset's original purchase price.",
      },
      {
        type: "callout",
        variant: "highlight",
        title: "★ Pair With a Donor Advised Fund",
        body: "Contributing appreciated assets to a Donor Advised Fund (DAF) captures the full deduction in the year of contribution while preserving flexibility to direct grants to specific charities over time. Particularly valuable in high-income years or in conjunction with deduction stacking.",
      },
    ],
  },

  deduction_stacking_schedule_a: {
    title: "Deduction Stacking on Schedule A",
    subtitle: "Concentrating Multiple Years of Deductions Into a Single Tax Year",
    intro:
      "The elevated standard deduction creates a high threshold that makes itemizing inefficient in most years. Deduction stacking — or bunching — concentrates multiple years' worth of charitable contributions, state taxes, and other deductible expenses into a single tax year, allowing you to itemize that year and take the standard deduction in alternating years.",
    sections: [
      { type: "heading", text: "The Donor Advised Fund Lever" },
      {
        type: "paragraph",
        text: "Donor Advised Funds are a particularly effective tool for this strategy: contribute two or three years of planned giving in one year, claim the large itemized deduction, then distribute grants to charities over time. The charities receive the same annual support — but the deduction has been efficiently concentrated to overcome the standard deduction threshold.",
      },
    ],
  },

  crt_clt: {
    title: "Charitable Remainder & Charitable Lead Trusts",
    subtitle: "IRC §664 / §170(f)(2) — Charitable Vehicles With Income or Estate Benefits",
    intro:
      "A Charitable Remainder Trust allows you to contribute appreciated assets into an irrevocable trust, receive an immediate partial charitable deduction, avoid upfront capital gains on the sale of the assets inside the trust, and receive an income stream for life or a term of years — with the remainder passing to charity.",
    sections: [
      { type: "heading", text: "The Charitable Lead Trust Mirror" },
      {
        type: "paragraph",
        text: "A Charitable Lead Trust works in reverse: charity receives the income stream first, and the remaining principal passes to your heirs, often at a reduced gift or estate tax value. Both structures are powerful tools when charitable intent is combined with a need for income or estate planning.",
      },
      {
        type: "callout",
        variant: "info",
        title: "■ Best Fit",
        body: "CRTs are particularly powerful for clients with a single highly-appreciated asset (concentrated stock, real estate at sale) who want to defer the gain, generate retirement income, and benefit charity. CLTs fit families with sizable estates and a desire to transfer wealth to heirs at compressed valuations.",
      },
    ],
  },

  zero_tax_state_residency: {
    title: "0% Tax State on Portfolio & Investment Income",
    subtitle: "Establishing Residency in a No-Income-Tax State",
    intro:
      "Nine states — including Nevada, Texas, Florida, Wyoming, and South Dakota — impose no individual income tax on wages, investment income, or capital gains. For high-income individuals with portfolio income, the annual state tax savings from establishing residency in a no-income-tax state can be substantial.",
    sections: [
      { type: "heading", text: "Establishing Genuine Domicile" },
      {
        type: "paragraph",
        text: "Proper establishment of domicile requires genuine physical presence, severing ties with the prior state, and attention to source-state rules for income earned from business activities or real property in higher-tax states. High-tax states (CA, NY) audit former residents aggressively — voter registration, driver's license, primary residence sale, time count, and family/business ties all factor into the domicile determination.",
      },
      {
        type: "callout",
        variant: "warn",
        title: "■ Source Income Traps",
        body: "Even after establishing residency in a no-tax state, income sourced to the former state remains taxable there: rental real estate, business activity, K-1 income from partnerships operating in the old state, and wages for work physically performed in the old state. Coordinate the move with operational decisions about where business is conducted.",
      },
    ],
  },

  primary_residence_exclusion: {
    title: "Primary Residence Sale Exclusion (§121)",
    subtitle: "IRC §121 — Excluding Up to $500K of Gain on a Home Sale",
    intro:
      "When you sell your primary residence, up to $250,000 of gain ($500,000 for married couples filing jointly) is excluded from federal income tax — provided you have owned and lived in the home as your primary residence for at least 2 of the 5 years preceding the sale.",
    sections: [
      { type: "heading", text: "Repeatable, With Timing Discipline" },
      {
        type: "paragraph",
        text: "This exclusion can be used repeatedly over a lifetime, though typically no more than once every two years. Planning the timing of a home sale to maximize the exclusion, and understanding how rental or home office use can affect it, is an important part of individual tax strategy. For homes that have been rented for a portion of ownership, the exclusion is reduced proportionally — careful tracking of non-qualified use periods is required.",
      },
    ],
  },

  tax_advantaged_accounts: {
    title: "HSA, 529, and Other Tax-Advantaged Accounts",
    subtitle: "IRC §223 (HSA) and §529 (College) — Stacking Tax-Free Compounding",
    intro:
      "An HSA is the only account in the tax code that provides a triple tax benefit: contributions are deductible (or pre-tax if made through payroll), growth inside the account is tax-deferred, and withdrawals for qualified medical expenses are completely tax-free. To contribute, you must be enrolled in a High Deductible Health Plan.",
    sections: [
      { type: "heading", text: "The HSA as a Long-Term Asset" },
      {
        type: "paragraph",
        text: "Once you turn 65, funds can be withdrawn for any purpose penalty-free (ordinary income tax applies to non-medical withdrawals, like a traditional IRA). For individuals who can afford to pay current medical expenses out-of-pocket, investing the HSA balance and allowing it to compound tax-free creates a powerful long-term retirement and healthcare asset.",
      },
      { type: "heading", text: "529 College Savings Accounts" },
      {
        type: "paragraph",
        text: "Contributions to a 529 plan are not federally deductible, but growth and distributions for qualified education expenses are completely tax-free. Many states offer a state income tax deduction for contributions. The account can be used for K-12 tuition (up to $10,000/year), college tuition, room and board, and rollovers to a Roth IRA for unused funds (subject to limits). Superfunding allows a lump-sum contribution of up to five years' worth of annual gift exclusion amounts in a single year, moving a significant amount out of your estate while retaining control of the assets.",
      },
    ],
  },

  plan_agi_aca: {
    title: "ACA Premium Tax Credit Planning",
    subtitle: "IRC §36B — Managing AGI to Preserve the Subsidy",
    intro:
      "If you obtain health insurance through the Affordable Care Act marketplace, the Premium Tax Credit subsidizes your premiums based on your household income relative to the federal poverty level. The credit phases out as income rises, so careful management of Adjusted Gross Income — through retirement contributions, timing of Roth conversions, harvesting losses, or deferring income — can preserve or increase the credit significantly.",
    sections: [
      { type: "heading", text: "Who Benefits Most" },
      {
        type: "paragraph",
        text: "For early retirees and self-employed individuals in particular, AGI management in this context can be worth thousands of dollars per year. The credit phases out as a percentage of household income above 400% of the federal poverty level (subject to current legislative extensions). Each dollar of AGI reduction near a cliff point can produce dramatic credit gains.",
      },
    ],
  },

  ltcg_holding_period: {
    title: "Hold Assets More Than 365 Days for LTCG Rates",
    subtitle: "IRC §1222 / §1(h) — The Most Straightforward Rate-Reduction Strategy",
    intro:
      "The single most straightforward rate-reduction strategy in the tax code: assets held longer than one year are taxed at long-term capital gains rates (0%, 15%, or 20%) rather than at ordinary income rates (up to 37%). For a taxpayer in the 37% ordinary income bracket, converting a short-term gain to a long-term gain by holding an asset just a few additional weeks or months can cut the tax rate nearly in half.",
    sections: [
      { type: "heading", text: "Coordinate With NIIT" },
      {
        type: "paragraph",
        text: "Combined with the Net Investment Income Tax of 3.8%, careful attention to holding periods is one of the highest-return-per-unit-of-effort planning moves available. A quarterly portfolio review identifies positions approaching the one-year mark where deferral of a planned sale by a few days or weeks produces immediate after-tax savings.",
      },
    ],
  },

  flp_income_asset_shifting: {
    title: "Income & Asset Shifting Through a Family Limited Partnership",
    subtitle: "IRC §704(e) — Valuation Discounts and Income Shifting",
    intro:
      "A Family Limited Partnership allows a senior family member to transfer income-producing and appreciating assets into a partnership structure, retaining a general partner interest while gifting or selling limited partner interests to children or trusts.",
    sections: [
      { type: "heading", text: "Valuation Discounts" },
      {
        type: "paragraph",
        text: "The valuation discounts available on minority, non-controlling limited partner interests (often 20–40%) reduce gift and estate tax exposure. Income can be shifted to family members in lower tax brackets, and the FLP provides a centralized governance structure for family wealth.",
      },
      {
        type: "callout",
        variant: "warn",
        title: "■ IRS Scrutiny Is Substantial",
        body: "Proper formation, documentation, and ongoing administration are essential to withstand IRS scrutiny. Valid business purpose, contemporaneous formation documents, real economic activity inside the partnership, and arms-length terms among partners are the defenses. Sham or post-funded partnerships consistently lose in tax court.",
      },
    ],
  },

  roth_conversion_timing: {
    title: "Roth IRA Conversion Strategy",
    subtitle: "IRC §408A(d)(3) — Filling Lower Brackets With Tax-Free Future Withdrawals",
    intro:
      "A Roth conversion moves funds from a traditional IRA or 401(k) — where distributions are taxed as ordinary income — into a Roth account where future growth and qualified distributions are permanently tax-free. The optimal time to convert is when your current-year taxable income is temporarily lower than your expected future income.",
    sections: [
      { type: "heading", text: "When to Convert" },
      {
        type: "paragraph",
        text: "Years with large deductions, business losses, retirement transition years, or years before Social Security and Required Minimum Distributions begin are prime conversion windows. Converting strategically over multiple years to fill lower tax brackets can permanently remove assets from the ordinary income tax system.",
      },
      {
        type: "callout",
        variant: "info",
        title: "■ Watch the Cliffs",
        body: "Roth conversions increase MAGI, which can affect IRMAA Medicare premiums, ACA premium tax credits, and the taxation of Social Security. A multi-year conversion ladder typically produces better results than a single large conversion.",
      },
    ],
  },

  trust_k1_election: {
    title: "Trust Election to Issue K-1",
    subtitle: "IRC §651 / §661 — Shifting Trust Income to Beneficiaries",
    intro:
      "Irrevocable trusts reach the top 37% federal income tax bracket at just $15,200 of taxable income (2024), making accumulation of income inside a trust extremely tax-inefficient. A properly structured distributing trust can pass income out to beneficiaries via Schedule K-1, taxing it at the beneficiary's lower individual rate instead of the trust's compressed rates.",
    sections: [
      { type: "heading", text: "Why This Compounds" },
      {
        type: "paragraph",
        text: "Trust-to-beneficiary income shifting — particularly when beneficiaries are in the 12% or 22% brackets — can produce dramatic annual tax savings on the same underlying income. Done consistently across years, the cumulative savings often exceed the cost of the entire trust administration.",
      },
    ],
  },

  tax_loss_harvesting: {
    title: "Tax Loss Harvesting on Investment Sales",
    subtitle: "Realizing Losses to Offset Gains and Ordinary Income",
    intro:
      "Tax loss harvesting involves strategically selling investment positions that are currently trading below your cost basis to realize a capital loss. Those losses offset capital gains elsewhere in your portfolio — or, up to $3,000/year, offset ordinary income.",
    sections: [
      { type: "heading", text: "The Wash-Sale Workaround" },
      {
        type: "paragraph",
        text: "The wash-sale rule prohibits repurchasing the same or substantially identical security within 30 days, but you can immediately reinvest in a similar (not identical) fund to maintain your market exposure. A proactive October–December portfolio review for harvesting opportunities is standard practice in any comprehensive tax plan.",
      },
    ],
  },

  crypto_no_wash_sale: {
    title: "Crypto Loss Harvesting — No Wash-Sale Restriction",
    subtitle: "IRC §1091 Does Not Apply to Digital Assets (Currently)",
    intro:
      "Unlike stocks and bonds, cryptocurrency is currently classified as property rather than a security — which means the wash-sale rule does not apply. This creates a unique tax planning opportunity: you can sell a crypto position at a loss to realize the tax loss, immediately repurchase the same asset the same day, and maintain your full economic position while banking the tax benefit.",
    sections: [
      { type: "heading", text: "Time-Limited Window" },
      {
        type: "paragraph",
        text: "Careful tracking of cost basis across wallets and exchanges is essential. As legislative proposals to extend wash-sale rules to crypto continue to surface, the window for this planning may narrow. Clients should harvest aggressively in years where they hold loss positions, with the understanding that the rule may be closed in future legislation.",
      },
    ],
  },

  shift_to_idgt: {
    title: "Shifting Appreciated Assets to an IDGT",
    subtitle: "Intentionally Defective Grantor Trust — Estate Removal With Income Tax Continuity",
    intro:
      "An IDGT is an irrevocable trust that is treated as owned by the grantor for income tax purposes (so the grantor continues paying income tax on trust income) but is outside the grantor's estate for estate tax purposes.",
    sections: [
      { type: "heading", text: "The Sale-to-IDGT Mechanic" },
      {
        type: "paragraph",
        text: "A grantor can sell appreciated assets to the IDGT in exchange for an installment note — without triggering capital gains on the sale, because a sale between a grantor and their own grantor trust is disregarded for tax purposes. The assets then appreciate outside the estate, and the grantor's payment of income tax on trust earnings is itself a tax-free gift to the trust beneficiaries.",
      },
      {
        type: "callout",
        variant: "highlight",
        title: "★ Compounding Benefit",
        body: "Every year the grantor pays the income tax on trust assets is a year the beneficiaries' share grows tax-free relative to a direct gift. Over a long planning horizon, the effective wealth transfer often exceeds the original transferred basis many times over.",
      },
    ],
  },

  deferred_sales_trust: {
    title: "Deferred Sales Trust",
    subtitle: "IRC §453 — Structured Installment Sale Through a Third-Party Trust",
    intro:
      "A Deferred Sales Trust is a structured arrangement in which a taxpayer sells a highly appreciated asset to a trust in exchange for an installment note, rather than selling directly to a third-party buyer. The trust then sells the asset to the end buyer and invests the proceeds.",
    sections: [
      { type: "heading", text: "Flexibility vs §1031" },
      {
        type: "paragraph",
        text: "The original seller receives payments over time per the note terms, deferring gain recognition across the payment schedule. Unlike a §1031 exchange, the proceeds are not required to be reinvested in like-kind property, giving the seller greater flexibility in how the capital is redeployed.",
      },
      {
        type: "callout",
        variant: "warn",
        title: "■ Promoter-Sensitive Structure",
        body: "The DST landscape includes legitimate practitioners and aggressive promoters. Diligence the trustee, the fee structure, the note terms, and IRS scrutiny history before recommending. Coordinate with the client's estate plan since the installment note becomes part of the estate.",
      },
    ],
  },

  life_insurance_death_benefit: {
    title: "Life Insurance Death Benefit Exclusion",
    subtitle: "IRC §101(a) — Tax-Free Wealth Transfer",
    intro:
      "Death benefits paid to beneficiaries of a life insurance policy are generally received income-tax-free under §101(a). For high-net-worth individuals, permanent life insurance — particularly when held inside an Irrevocable Life Insurance Trust (ILIT) — can serve as a powerful vehicle for tax-free wealth transfer, estate liquidity, and income replacement.",
    sections: [
      { type: "heading", text: "Inside Buildup" },
      {
        type: "paragraph",
        text: "The inside buildup of cash value within a policy also grows on a tax-deferred basis and can be accessed tax-free through policy loans. Properly structured permanent life insurance is one of the few asset classes that combines tax-deferred growth, tax-free access, and tax-free transfer at death.",
      },
    ],
  },

  ilit: {
    title: "Irrevocable Life Insurance Trust (ILIT)",
    subtitle: "Removing the Death Benefit From the Taxable Estate",
    intro:
      "When a life insurance policy is owned by an ILIT rather than by the insured directly, the death benefit is removed from the insured's taxable estate. This can save estate taxes on large policies for individuals whose estates exceed the federal exemption.",
    sections: [
      { type: "heading", text: "Structure" },
      {
        type: "paragraph",
        text: "The ILIT owns the policy, pays the premiums using annual gift exclusion contributions from the insured, and distributes proceeds to beneficiaries outside the probate process. Proper structuring requires careful attention to the Crummey notice rules and trustee administration. Existing policies can be transferred but trigger the three-year inclusion rule under §2035 — best to have the ILIT acquire new policies directly.",
      },
    ],
  },

  // ───── Business ─────
  cash_vs_accrual_timing: {
    title: "Cash vs. Accrual Accounting — Timing Income & Deductions",
    subtitle: "IRC §448 / §481(a) — Method Choice Drives Recognition",
    intro:
      "The choice between cash-basis and accrual-basis accounting directly controls when income is recognized and when expenses are deducted. A cash-basis business can accelerate deductions into the current year (prepaying expenses, delaying invoicing into January) and defer income.",
    sections: [
      { type: "heading", text: "The Year-End Lever" },
      {
        type: "paragraph",
        text: "An accrual-basis business follows different rules but can take advantage of recurring item elections and end-of-year expense accruals. Switching methods — or electing the most favorable method for your size and structure — requires a §481(a) adjustment analysis but can generate significant timing benefits.",
      },
      {
        type: "callout",
        variant: "highlight",
        title: "★ Highest-ROI Planning Item",
        body: "Maximizing the flexibility within your accounting method is one of the highest-ROI planning items in any given year. OBBBA expanded cash-method eligibility (~$31M gross receipts test) — many businesses that previously had to use accrual can now elect cash and capture the year-end timing flexibility.",
      },
    ],
  },

  entity_classification_se_tax: {
    title: "Entity Classification & Self-Employment Tax Reduction",
    subtitle: "IRC §1402 — Structuring Compensation to Reduce SE Exposure",
    intro:
      "The legal structure of your business — sole proprietorship, S-Corp, C-Corp, or partnership — determines not only your income tax rate, but also your self-employment (SE) tax exposure. An S-Corp election, for example, allows you to split income between a reasonable W-2 salary (subject to payroll taxes) and pass-through distributions (not subject to SE tax), potentially saving $10,000–$30,000+ annually in SE tax on the same profit level.",
    sections: [
      { type: "heading", text: "Foundational Decision" },
      {
        type: "paragraph",
        text: "The right entity structure is one of the foundational decisions in business tax planning and should be re-evaluated as income grows. Reasonable compensation must be substantiated (RC Reports, BLS data, or a comparable study); too-low salaries are an IRS exam priority. Coordinate with §199A wage limit since W-2 wages affect the QBI deduction calculation.",
      },
    ],
  },

  hire_kids: {
    title: "Hire Your Children Through the Business",
    subtitle: "Shifting Income to a 0% Bracket With Roth IRA Funding",
    intro:
      "If you own a business (particularly a sole proprietorship or single-member LLC), wages paid to your minor children for legitimate work are deductible to the business and potentially tax-free to the child — the standard deduction shelters up to $14,600 (2024) of earned income at the child level with no federal income tax owed.",
    sections: [
      { type: "heading", text: "Roth IRA Funding Multiplier" },
      {
        type: "paragraph",
        text: "The wages also fund a Roth IRA contribution for the child. Properly documented and reasonably compensated, this strategy can shift $10,000–$15,000 of income from your 37% bracket to a 0% bracket annually — and seed a Roth IRA with decades of tax-free growth ahead of it.",
      },
      {
        type: "callout",
        variant: "warn",
        title: "■ Documentation Is Non-Negotiable",
        body: "Time sheets, actual work performed, FMV wage rates, written job description, W-2 filing. The IRS audits these arrangements when the child's wages are unreasonable for the age, work, or business size. Sole prop / spousal LLC structure preserves the FICA exemption for children under 18.",
      },
    ],
  },

  previously_disallowed_qbi: {
    title: "§199A Qualified Business Income (QBI) Deduction",
    subtitle: "IRC §199A — Up to 20% Deduction on Pass-Through Income",
    intro:
      "Owners of pass-through businesses — S-Corps, partnerships, sole proprietorships — can deduct up to 20% of qualified business income, subject to W-2 wage and property limitations above the taxable income threshold ($383,900 married filing jointly in 2024).",
    sections: [
      { type: "heading", text: "Structuring to Preserve the Deduction" },
      {
        type: "paragraph",
        text: "Properly structuring your entity, W-2 payroll, and qualified property ensures you maximize this deduction before it is lost. For an owner generating $400,000 in pass-through income, the QBI deduction can be worth $29,600 per year in tax savings — and it is forfeited if payroll structure or entity form is not optimized.",
      },
      {
        type: "callout",
        variant: "info",
        title: "■ OBBBA Made It Permanent",
        body: "The §199A deduction was made permanent under the One Big Beautiful Bill Act. This removes the planning urgency that surrounded the original 2025 sunset but does not change the substantive structuring opportunities. Aggregation elections, SSTB phase-out planning, and W-2 wage optimization all continue to matter.",
      },
    ],
  },

  section_831b_captive: {
    title: "§831(b) Reinsurance Company",
    subtitle:
      "Tax-Deferred Self-Insurance Plan — Premium Deduction & Reserve Accumulation Strategy",
    intro:
      "A §831(b) Reinsurance Company — administered by SRA 831(b) Admin (Strategic Risk Alternatives) and domiciled on the sovereign lands of the Modoc Nation in Oklahoma — is a privately owned, licensed reinsurance company formed by a business owner to cover genuine, documented business risks that are under- or uninsured by conventional insurance. Under IRC §831(b), qualifying small insurance companies may elect to be taxed only on investment income — not on premiums received — provided annual net written premiums do not exceed $2,900,000 (2026 indexed cap per Rev. Proc. 2025-32).",
    sections: [
      {
        type: "callout",
        variant: "info",
        title: "★ IRS Audit Confidence — More Likely Than Not to Be Sustained (>51%)",
        body: "Audit confidence scale: >51% More likely than not · >70% Should be sustained · >90% Will be sustained. When properly structured, actuarially supported, and administered by SRA — an experienced plan administrator with a completed IRS promoter audit — this strategy has strong audit defensibility. Form 8886 disclosure is mandatory every year.",
      },

      { type: "heading", text: "Ideal Tax Client" },
      {
        type: "paragraph",
        text: "Profitable private business owners grossing approximately $2 million or more annually who face under- or uninsured business risks. Ideal for S-Corp, C-Corp, and LLC owners across all industries — especially those with risk concentrations in areas not fully covered by commercial insurance. The business owner must be willing to operate the Reinsurance Company as a legitimate insurance entity under the oversight of the Modoc Nation Insurance Department.",
      },

      { type: "heading", text: "Strategy Overview" },
      {
        type: "paragraph",
        text: "The operating business pays tax-deductible insurance premiums to the Reinsurance Company. Those premiums are received tax-free under the §831(b) election, and reserves accumulate and grow within the entity. SRA describes this as building a powerful way to set aside tax-deferred funds to protect businesses from under- or uninsured risk. When structured, documented, and administered according to SRA's compliance framework — with genuine risk transfer, actuarially supported premiums, and full regulatory oversight — the plan delivers both immediate tax deductions for the operating company and long-term, tax-advantaged reserve accumulation.",
      },
      {
        type: "paragraph",
        text: "The IRS has designated certain §831(b) arrangements as Transactions of Interest (Notice 2016-66) and placed abusive structures on its Dirty Dozen list. SRA distinguishes itself through a risk-management-first approach, having successfully completed an IRS promoter audit (August 2022 – July 2024) with no operational changes required. Clients who follow SRA's guidelines receive a warranty providing legal and accounting support in the event of an IRS examination.",
      },

      { type: "heading", text: "How It Works" },
      {
        type: "table",
        headers: ["Step", "Phase", "What Happens"],
        rows: [
          ["1", "Qualification Meeting", "SRA reviews the business owner's gross revenue (~$2M+ required), industry, and risk profile to determine whether a §831(b) Plan is appropriate. A risk assessment identifies the most significant uninsured or underinsured risks facing the business."],
          ["2", "Discovery & Education", "The business owner meets with an SRA subject matter expert to review the plan in detail, ask questions, and have the opportunity to speak with other business owners in similar industries who have implemented the program."],
          ["3", "Policy Design", "SRA collaborates with the business owner and an underwriter to determine the optimal policy profile — identifying insurable risks such as brand protection, key employee loss, data breach, business interruption, contract default, or political risk."],
          ["4", "Plan Formation", "The Reinsurance Company is formed on the sovereign lands of the Modoc Nation (a federally recognized tribal jurisdiction in Oklahoma), which established its captive insurance domicile in 2018. The Modoc Nation Insurance Department provides regulatory oversight. Formation typically takes less than one month."],
          ["5", "Premium & §831(b) Election", "The operating business pays annual insurance premiums to the Reinsurance Company. Premiums are deducted as ordinary business expenses. The Reinsurance Company makes its §831(b) election, excluding premium income from federal taxable income (up to $2,900,000 in 2026)."],
          ["6", "Annual Administration", "SRA handles all ongoing administration — plan document preparation, risk assessments, transaction approvals, compliance monitoring, Form 8886 filing, 1099-DIV, annual tax return (Form 1120-PC), meeting minutes, claim services, and annual renewals."],
          ["7", "Reserve Growth & Distributions", "Premium reserves accumulate within the Reinsurance Company and are invested for growth. Over time, business owners may access reserves through shareholder dividends or loans. When legitimate covered losses occur, claims are filed and paid, validating the insurance character of the arrangement."],
        ],
        theme: "navy",
      },

      { type: "heading", text: "Tax Mechanics" },
      { type: "paragraph", text: "Premium Deduction — IRC §162: Insurance premiums paid by the operating company to the Reinsurance Company are deductible as ordinary and necessary business expenses, provided they are arm's-length, actuarially supported, and the Reinsurance Company constitutes a true insurance arrangement involving both risk shifting and risk distribution." },
      { type: "paragraph", text: "Premium Exclusion — IRC §831(b): The Reinsurance Company elects to be taxed only on investment income, not on premiums received. For 2026, net written premiums must not exceed $2,900,000 (per Rev. Proc. 2025-32) to qualify for the election. Premium reserves accumulate tax-free, subject only to tax on investment earnings." },
      { type: "paragraph", text: "Modoc Nation Domicile: The Reinsurance Company is formed and regulated under the laws of the Modoc Nation — a federally recognized sovereign tribal jurisdiction in northeast Oklahoma. As a domestic domicile, the Modoc Nation offers the confidentiality associated with offshore companies combined with the convenience of an onshore U.S. jurisdiction. The Modoc Insurance Department has regulated 700+ reinsurance companies since establishing its domicile in 2018." },
      { type: "paragraph", text: "Risk Transfer & Risk Distribution: For federal tax purposes, an arrangement constitutes insurance only if it involves both (1) risk shifting — the insured transfers risk to an independent entity legally obligated to pay claims — and (2) risk distribution — the insurer pools a statistically meaningful number of independent risk exposures. SRA uses pooling arrangements among its 1,000+ administered plans to satisfy risk distribution requirements." },
      { type: "paragraph", text: "IRS Loss Ratio Thresholds (Jan. 2025 Final Regs): New IRS regulations effective in 2025 introduced loss ratio thresholds for §831(b) captives: plans with loss ratios below 30% may be designated as listed transactions; those between 30% and 60% may be transactions of interest. SRA's risk-management-first design approach targets meaningful, real claims activity to maintain compliant loss ratios." },
      { type: "paragraph", text: "Reserve Access & Exit — IRC §1222: After sufficient operating history, accumulated Reinsurance Company reserves can be accessed by the owner as shareholder dividends or loans. Properly structured distributions may qualify for capital gains or dividend treatment, providing a meaningful rate differential compared to ordinary income." },

      { type: "heading", text: "Types of Risks Commonly Covered" },
      {
        type: "bullets",
        items: [
          "Brand Protection — reputational harm, PR expenses, legal fees to protect business image",
          "Key Employee Loss — financial recovery for unexpected departure or disability of critical personnel",
          "Data Breach — recovery expenses, legal fees, notification costs from cyber events",
          "Business Interruption — payroll, rent, and operating expenses during unexpected downtime",
          "Contract Default — financial protection when clients or subcontractors fail contractual obligations",
          "Political Risk — losses from government actions, regulatory shutdowns, or pandemic-related closures",
          "Deductible Reimbursement — reserve to cover large commercial policy deductibles when triggered",
          "Custom Warranty Programs — self-insured product/service warranty reserves",
          "Tenant Assurance — property owners managing renter liability risk",
          "Medical Allied Reinsurance (MARC) — bolt-on for businesses with self-funded health plans",
        ],
      },

      { type: "heading", text: "Illustrative Example — $500,000 Annual Premium, 37% Tax Bracket" },
      {
        type: "table",
        headers: ["Line Item", "Amount"],
        rows: [
          ["Annual premium paid by operating company (2026 cap: $2.9M)", "$500,000"],
          ["Estimated federal tax deduction (37% bracket)", "~$185,000 tax savings"],
          ["Reinsurance Company income from premiums (tax-free under §831(b))", "$500,000 excluded from CIC income"],
          ["SRA Retained Liability Premium (RLP) — 8% of $500K (Breakpoint 2)", "$40,000/yr"],
          ["SRA Annual Maintenance Fee (flat, deductible)", "$6,000/yr"],
          ["Total SRA annual cost (RLP + maintenance, both deductible)", "$46,000/yr"],
          ["Net first-year federal tax benefit ($185K savings less $46K SRA cost)", "~$139,000"],
          ["Captive reserve accumulation — Year 1 (after costs, 6% growth)", "~$487,000"],
          ["10-year cumulative reserve (6% growth, $500K/yr net premium)", "~$6,200,000"],
          ["10-year cumulative federal tax savings (37%)", "~$1,850,000"],
          ["Potential exit: shareholder dividends or loans from reserve", "Capital gains / dividend treatment at exit"],
        ],
        theme: "navy",
      },
      {
        type: "paragraph",
        text: "Illustrative only. Based on $500,000 annual premium, 37% federal tax bracket, and actual SRA costs of $46,000/yr ($40,000 RLP at Breakpoint 2 rate of 8% + $6,000 annual maintenance fee). Both fees are deductible as ordinary business expenses. SRA fees vary by premium level — see fee schedule below. Actual results depend on premium level, tax rate, investment performance, loss experience, and IRS acceptance. Always run a client-specific projection before recommending.",
      },

      { type: "heading", text: "Eligibility Requirements" },
      {
        type: "bullets",
        items: [
          "U.S. business owner grossing approximately $2 million or more annually",
          "Faces genuine under- or uninsured business risks identifiable by actuarial analysis",
          "Willingness to operate the Reinsurance Company as a legitimate insurance entity",
          "Commitment to annual governance: policy reviews, board oversight, claims administration",
          "Form 8886 Reportable Transaction Disclosure filed annually with tax return",
          "Annual tax return filed for the Reinsurance Company (Form 1120-PC)",
          "Follow SRA's plan guidelines to qualify for the audit warranty",
        ],
      },

      {
        type: "callout",
        variant: "highlight",
        title: "★ Risk Distribution Through SRA Pooling",
        body: "SRA satisfies the IRS risk distribution requirement through a formal pooling arrangement among its 1,000+ administered Reinsurance Companies. Each participating company contributes to and shares in a pool of claims from other unrelated reinsurance companies. Maximum pooled claims exposure cannot exceed 51% of the total premium paid by the participating Reinsurance Company in any given policy year. The 10-year historical average of actual pooled claims paid has been less than 1.2% of premium paid — demonstrating that real-world pool exposure has been minimal. State tax conformity: not all states conform to federal captive premium deductions — a state-level projection should be run for each client.",
      },

      { type: "heading", text: "Cautions & Risk Factors" },
      { type: "paragraph", text: "IRS scrutiny is the primary risk. The §831(b) plan is a designated IRS Transaction of Interest. Form 8886 must be filed with both the operating company's and the Reinsurance Company's annual tax returns. Failure to file triggers automatic penalties under §6707A regardless of whether the underlying strategy is legitimate." },
      { type: "paragraph", text: "Loss ratio thresholds are now codified. IRS final regulations (January 2025) classify plans with loss ratios below 30% as listed transactions and those between 30–60% as transactions of interest. SRA's compliance framework is specifically designed to target meaningful claims activity and maintain ratios above these thresholds." },
      { type: "paragraph", text: "Claims must be genuine and contemporaneously documented. Reinsurance companies that never pay claims or show only token claims are a significant audit flag. Legitimate claims activity is the most important proof of insurance substance." },
      { type: "paragraph", text: "The Modoc Nation domicile is a domestic U.S. jurisdiction. As a federally recognized tribal sovereign, the Modoc Nation operates its own insurance department with its own regulations. This is a domestic arrangement — not offshore — though some IRS agents may be unfamiliar with tribal domiciles and require additional explanation." },
      { type: "paragraph", text: "Distributions and loans are not available in Year 1. Access to accumulated reserves through shareholder dividends or loans is restricted during the first policy year. Once sufficient funds have accumulated and the plan has demonstrated a proper operating history, distributions and loans become available beginning in Year 2 and beyond, subject to SRA's plan guidelines and applicable insurance regulations." },
      { type: "paragraph", text: "Legislative risk is ongoing. Congress has repeatedly considered modifications to §831(b). SRA actively lobbies through the 831(b) Institute. Clients should understand that future legislative changes could affect the program's economics." },
      { type: "paragraph", text: "No economic return if deduction is disallowed. Unlike strategies with independent investment returns, if the premium deduction is fully disallowed, the primary benefit is lost. Formation and annual administration costs become sunk costs. SRA's audit warranty mitigates but does not eliminate this risk." },

      { type: "heading", text: "Plan Administrator — SRA 831(b) Admin" },
      { type: "paragraph", text: "SRA 831(b) Admin (Strategic Risk Alternatives) is the largest §831(b) plan administrator in the country, with 1,000+ plans administered since its founding in 2008. Founded by Van Carlson, a former property & casualty professional with 30+ years of experience, SRA approaches the §831(b) program through a risk management lens — not merely as a tax strategy. SRA's stated goal is to make 831(b) an ordinary business practice ubiquitous with 401(k). All plans are domiciled on the sovereign lands of the Modoc Nation in Oklahoma." },
      {
        type: "table",
        headers: ["Detail", "Information"],
        rows: [
          ["Company", "SRA 831(b) Admin (Strategic Risk Alternatives)"],
          ["Website", "831b.com"],
          ["Address", "776 E. Riverside Drive, Suite 200, Eagle, ID 83616"],
          ["Phone", "208-424-2249"],
          ["Email", "sra@831b.com"],
          ["Founder", "Van Carlson (former P&C professional, 30+ years experience)"],
          ["Founded / Plans Administered", "2008 · 1,000+"],
          ["Domicile", "Modoc Nation (federally recognized tribal sovereign), NE Oklahoma — established as captive domicile in 2018; home to 700+ reinsurance companies"],
          ["IRS Audit", "Successfully completed IRS promoter audit (Aug 2022 – July 2024) with no operational changes required"],
          ["Warranty", "Clients who follow SRA guidelines receive legal and accounting support in the event of an IRS audit"],
          ["Industry Advocacy", "Founding member of the 831(b) Institute; active lobbying in Washington D.C."],
          ["Accolades", "Inc. 5000 (2023); Business Insurance Best Places to Work (2024, 2025); Forbes Business Council member"],
        ],
        theme: "navy",
      },

      { type: "heading", text: "Financial Forecast — 10-Year Horizon" },
      { type: "paragraph", text: "Based on $500,000 annual premium, 37% effective federal tax rate, and actual SRA costs of $46,000/yr ($40,000 RLP at Breakpoint 2 + $6,000 annual maintenance fee, both deductible). Net annual deposit to the captive investment account is $454,000 ($500K premium less $46K SRA fees). Investment earnings are calculated at 7% on the prior year-end balance, with each year's new deposit added at year-end. The 2026 annual premium cap under §831(b) is $2,900,000, providing significant headroom above the $500,000 modeled here." },
      {
        type: "table",
        headers: [
          "Year",
          "Premium",
          "Tax Savings (37%)",
          "SRA Cost",
          "Net Deposit",
          "Investment Earnings (7%)",
          "Year-End Balance",
          "Cumulative Tax Savings",
        ],
        rows: [
          ["1", "$500,000", "$185,000", "$46,000", "$454K", "—", "$454K", "$185K"],
          ["2", "$500,000", "$185,000", "$46,000", "$454K", "$32K", "$940K", "$370K"],
          ["3", "$500,000", "$185,000", "$46,000", "$454K", "$66K", "$1.46M", "$555K"],
          ["4", "$500,000", "$185,000", "$46,000", "$454K", "$102K", "$2.02M", "$740K"],
          ["5", "$500,000", "$185,000", "$46,000", "$454K", "$141K", "$2.61M", "$925K"],
          ["6", "$500,000", "$185,000", "$46,000", "$454K", "$183K", "$3.25M", "$1,110K"],
          ["7", "$500,000", "$185,000", "$46,000", "$454K", "$227K", "$3.93M", "$1,295K"],
          ["8", "$500,000", "$185,000", "$46,000", "$454K", "$275K", "$4.66M", "$1,480K"],
          ["9", "$500,000", "$185,000", "$46,000", "$454K", "$326K", "$5.44M", "$1,665K"],
          ["10", "$500,000", "$185,000", "$46,000", "$454K", "$381K", "$6.27M", "$1,850K"],
          ["10-Yr", "$5,000,000", "$1,850,000", "$460,000", "$4,540,000", "$1.73M", "$6.27M", "$1,850K"],
        ],
        theme: "navy",
      },
      { type: "paragraph", text: "Illustrative projections only. Tax savings received upon filing of operating company's annual return. SRA costs of $46,000/yr ($40,000 RLP + $6,000 maintenance, both deductible) are netted before the $454,000 annual deposit to the captive investment account. Investment earnings reflect 7% applied to the prior year-end balance only — the new year's deposit earns beginning the following year. Year-End Captive Balance = principal deposits + accumulated investment earnings. Does not account for income taxes on investment earnings within the Reinsurance Company, state tax treatment, or potential IRS challenge. Actual results will vary." },

      { type: "heading", text: "SRA Fee Schedule — Retained Liability Premium (RLP)" },
      { type: "paragraph", text: "The Retained Liability Premium (RLP) is assessed on all new premium contributions and is calculated based on the total premium for each policy year. Effective January 1, 2025, and subject to change each policy year." },
      {
        type: "table",
        headers: ["Breakpoint", "Total Premium", "Retained Liability Premium (RLP)"],
        rows: [
          ["Breakpoint 1", "$0 – $399,999", "10%"],
          ["Breakpoint 2", "$400,000 – $649,999", "8%"],
          ["Breakpoint 3", "$650,000 – $899,999", "7%"],
          ["Breakpoint 4", "$900,000 – $1,149,999", "6%"],
          ["Breakpoint 5", "$1,150,000 – $1,499,999", "5%"],
          ["Breakpoint 6", "$1,500,000 – $1,999,999", "4%"],
          ["Breakpoint 7", "$2,000,000+", "3%"],
        ],
        theme: "navy",
      },
      { type: "paragraph", text: "Annual Maintenance Fee: $6,000 due each calendar year to SRA 831(b) Admin. Both the RLP and the Annual Maintenance Fee are paid to SRA and are deductible as ordinary and necessary business expenses under IRC §162. For a $500,000 annual premium (Breakpoint 2), total SRA fees are $46,000/yr ($40,000 RLP + $6,000 maintenance)." },

      { type: "heading", text: "Relevant Code Sections & Authorities" },
      {
        type: "bullets",
        items: [
          "IRC §831(b) — Small insurance company election; exclusion of premium income from taxable income (2026 cap: $2,900,000 per Rev. Proc. 2025-32)",
          "IRC §162 — Ordinary and necessary business expense deduction for insurance premiums paid to the Reinsurance Company",
          "IRC §831(a) — General insurance company taxation; investment income taxable even under §831(b) election",
          "IRC §1222 — Capital gain/loss treatment on Reinsurance Company liquidation or shareholder distributions",
          "Rev. Proc. 2025-32 — IRS inflation adjustment setting 2026 §831(b) premium cap at $2,900,000",
          "IRC §6707A — Penalties for failure to disclose listed/reportable transactions on Form 8886",
          "IRC §6662 — Accuracy-related penalties; 40% gross valuation misstatement penalty for premium overstatement",
          "IRS Notice 2016-66 — §831(b) micro-captives designated as Transactions of Interest",
          "IRS Final Regs (Jan 2025) — Loss ratio thresholds: <30% = listed transaction; 30–60% = transaction of interest",
          "Rev. Rul. 2002-90 — Risk distribution requirements; brother-sister group of 12+ entities sufficient",
          "Rent-A-Center v. Commissioner — Single-entity captive held to lack risk distribution",
        ],
      },
    ],
  },

  augusta_rule: {
    title: "Augusta Rule — Home Office Rental Exclusion",
    subtitle: "IRC §280A(g) — 14-Day Tax-Free Home Rental",
    intro:
      "Under §280A(g), a homeowner can rent their personal residence to their own business for up to 14 days per year, and the rental income received is excluded from the homeowner's personal gross income. The business simultaneously deducts the rental payment as an ordinary business expense.",
    sections: [
      { type: "heading", text: "Applied to Legitimate Business Use" },
      {
        type: "paragraph",
        text: "Applied to legitimate business meetings or planning sessions held at the home, this can generate a deduction at the business level while the corresponding income is tax-free at the individual level. Proper documentation of the business purpose, market rental rate, and meeting records is required.",
      },
      {
        type: "callout",
        variant: "warn",
        title: "■ The 14-Day Limit Is Strict",
        body: "Renting for 15 or more days converts the property to rental property for the entire year, defeating the §280A(g) exclusion. Document each day's business purpose, the market FMV rate via comparable hotel/event venues, and the formal board minutes or meeting agenda contemporaneously.",
      },
    ],
  },

  depreciation_timing_481a: {
    title: "Bonus Depreciation & §179 Timing",
    subtitle: "IRC §168(k) / §179 — Shifting Deductions Across Tax Years",
    intro:
      "Businesses that purchase equipment, machinery, vehicles, furniture, or certain software may elect to deduct the full cost in the year of purchase rather than depreciating it over multiple years. Section 179 allows expensing up to the annual cap of qualified property. Bonus depreciation under §168(k) allows first-year expensing on qualifying assets with no dollar cap.",
    sections: [
      { type: "heading", text: "The Year-End Decision" },
      {
        type: "paragraph",
        text: "Proper timing of asset purchases — placing assets in service before December 31 vs. January 1 — can shift hundreds of thousands of dollars of deductions between tax years, dramatically impacting your current-year tax bill. For seasonal businesses, an asset placed in service in late December captures the full year's deduction in a year of high income.",
      },
    ],
  },

  max_employer_retirement: {
    title: "Maximize Employer Retirement Contributions (Including DB/Cash Balance)",
    subtitle: "IRC §401(a) — From Solo 401(k) to Defined Benefit Stacking",
    intro:
      "For a high-income business owner with few or no employees, a defined benefit or cash balance pension plan allows annual tax-deductible contributions that far exceed 401(k)/SEP-IRA limits — in many cases $100,000–$300,000+ per year depending on age and income.",
    sections: [
      { type: "heading", text: "Single Largest Deduction for Owner-Operators" },
      {
        type: "paragraph",
        text: "Contributions are fully deductible in the year made, reducing self-employment income and taxable income immediately. The plan assets grow tax-deferred until distribution. For an owner in their 50s, this is often the single largest deduction available and also builds a retirement account unavailable through any other vehicle.",
      },
      {
        type: "callout",
        variant: "info",
        title: "■ Plan Setup Timing",
        body: "Solo 401(k) plans must be adopted by the last day of the plan year for elective deferrals. Defined benefit and cash balance plans have more flexibility under SECURE 2.0 — adoption is permitted up to the extended due date of the return for plan-year contributions.",
      },
    ],
  },
};

// Merge secondary briefs alongside the rich PDF-style briefs
Object.assign(CLIENT_BRIEFS, SECONDARY_BRIEFS);

for (const s of STRATEGIES) {
  if (CLIENT_BRIEFS[s.id]) s.clientBrief = CLIENT_BRIEFS[s.id];
}

export function getClientBrief(strategyId) {
  return CLIENT_BRIEFS[strategyId] || null;
}

// Compute concrete deadline date for a strategy + tax year + state
export function deadlineDateFor(strategy, taxYear, state = null) {
  const t = strategy.timing || {};
  const y = taxYear || new Date().getFullYear();
  switch (t.kind) {
    case "year_end":
      return new Date(y, 11, 31); // Dec 31
    case "specific_date": {
      if (!t.date) return new Date(y, 11, 31);
      const [mo, da] = t.date.split("-").map((n) => parseInt(n, 10));
      if (isNaN(mo)) return new Date(y, 11, 31);
      return new Date(y, mo - 1, da || 1);
    }
    case "original_due":
      // Individual returns: 4/15 of next year
      return new Date(y + 1, 3, 15);
    case "extended_due":
      // Individual returns: 10/15 of next year
      return new Date(y + 1, 9, 15);
    case "prior_to_filing":
      return new Date(y + 1, 3, 15); // ~original due
    case "pre_transaction":
    case "ongoing":
    default:
      return null;
  }
}

// ─── Custom strategy registry ───────────────────────────────────────────
// Firm users can create custom strategies that extend the seeded library.
// They live in Supabase but get cached at module level so the synchronous
// getStrategy(id) lookup keeps working everywhere it is already used.
let _customStrategies = [];

export function setCustomStrategies(arr) {
  _customStrategies = Array.isArray(arr) ? arr : [];
}

export function getCustomStrategies() {
  return _customStrategies;
}

export function getAllStrategies() {
  return [...STRATEGIES, ..._customStrategies];
}

export function isBuiltInStrategy(id) {
  return STRATEGIES.some((s) => s.id === id);
}

export function isCustomStrategy(id) {
  return _customStrategies.some((s) => s.id === id);
}

// Helper indexers
export function strategiesByCategory(catId) {
  return getAllStrategies().filter((s) => s.categories?.includes(catId));
}

export function strategiesByGoal(goalId) {
  return getAllStrategies().filter((s) => s.goals?.includes(goalId));
}

export function strategiesByEntity(entityId) {
  return getAllStrategies().filter((s) => s.entityTypes?.includes(entityId));
}

export function getStrategy(id) {
  return (
    STRATEGIES.find((s) => s.id === id) ||
    _customStrategies.find((s) => s.id === id) ||
    null
  );
}

export default STRATEGIES;
